import { useState, useEffect, useCallback, useRef, Fragment, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileText, Clock, ShoppingCart, Plus, Pencil, Trash2,
  Save, X, FileSpreadsheet, BarChart3, ChevronDown, ChevronRight, RefreshCw, Table2, GripVertical, Download, CalendarRange
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { CollapsibleCard } from '../../components/ui/collapsible-card';
import DocumentHeader, { HeaderBadge } from '../../components/DocumentHeader';
import StatusSelect from '../../components/StatusSelect';
import { api } from '../../core/apiClient';
import { badgeFor } from '../compras/NodeCostTree';
import { groupByChapter, SENSE_CHAPTER } from '../compras/nodeUtils';
import { fmt2, fmt3, type DetallDisplayRow, sanitizeFilename, type PartidaSummary, exportLiniesAmbPartidesToExcel } from '../compras/detallExcelExport';
import { MaterialNodeSearchInput, MaterialNodeSearchModal, type MaterialSearchResult, type NodeSearchResult } from '../compras/MaterialNodeSearchInput';

// ── Cronograma: helpers de dates (mateix patró que Planificacio.tsx) ───────
// toISOString() converteix a UTC — amb el navegador en zona horària per
// davant d'UTC (p.ex. Espanya, UTC+1/+2), la mitjanit local "cau" al dia
// anterior en UTC i slice(0,10) es queda amb aquesta data anterior. Per
// aixo es formata amb els getters LOCALS (getFullYear/getMonth/getDate).
const toISO = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDaysISO = (iso: string, n: number): string => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return toISO(d);
};
const diffDaysISO = (a: string, b: string): number =>
  Math.round((new Date(b + 'T00:00:00').getTime() - new Date(a + 'T00:00:00').getTime()) / 86400000);
const CRONO_WEEKS_VISIBLE = 12;
const CRONO_VISIBLE_DAYS = CRONO_WEEKS_VISIBLE * 7;
const CRONO_DAY_PX = 30;
const CRONO_GRID_WIDTH = CRONO_VISIBLE_DAYS * CRONO_DAY_PX;
const CRONO_MONTH_LABELS = ['Gen', 'Feb', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Oct', 'Nov', 'Des'];

interface WorkOrder {
  id: string;
  orderNumber: string;
  name: string;
  description?: string;
  units: number;
  unitType: string;
  prevHours: number;
  realHours: number;
  prevMaterials: number;
  realPurchases: number;
  realLabor: number;
  status: string;
  typeId: number;
  type?: string;
  nodeId?: string;
  projectBudgetLineId?: string;
  budgetLineIndex?: number;
  groupedBudgetLines?: { projectBudgetLineId: string; budgetLineIndex: number }[];
  assignedToId?: string;
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
}

interface EscandallOption {
  id: string;
  code: string | null;
  name: string;
}

// Línia individual del desglose d'un escandall (mateixa forma que
// NodeDetailLine a BudgetForm.tsx) — materials/mà d'obra/serveis que
// configuren la línia de pressupost, cada una convertible en OT.
interface EscandallDetailLine {
  childType?: string;
  nature?: string;
  description?: string;
  referencia?: string;
  detail?: string;
  unit?: string;
  chapter?: string;
  quantity?: number;
  dimensionA?: number;
  dimensionB?: number;
  dimensionH?: number;
  resolvedUnitCost?: number;
  marge?: number;
  descompte?: number;
  pesTotal?: number;
  // Pes unitari (kg/unitat de mesura) del material/escandall vinculat —
  // mateix patró que BudgetForm.tsx, nomes per recalcular pesTotal quan
  // canvia Quantitat/A/B/H (vegeu updateSublineDraft).
  weightKgPerUnit?: number;
  observations?: string;
  isPriceLocked?: boolean;
  elementImageUrl?: string | null;
  childMaterialId?: string | null;
  childNodeId?: string | null;
  // Planificació del cronograma (pestanya "Cronograma") — nomes te sentit
  // quan chapter esta informat. plannedStartDate/plannedEndDate es el format
  // antic (un sol periode); plannedSegments el nou (2026-10-19, múltiples
  // periodes independents per línia) — vegeu cronoItemSegments més avall,
  // que llegeix plannedSegments si n'hi ha i si no cau al format antic.
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
  plannedSegments?: { id: string; startDate: string; endDate: string }[] | null;
  // Marca si aquest ítem s'ha "importat" al Cronograma — el Cronograma
  // comença buit en crear l'expedient; nomes les línies que l'usuari tria
  // explícitament hi apareixen (vegeu buildCronoChapters). No toca cost ni
  // estructura, nomes decideix si es mostra a planificar.
  inCronograma?: boolean;
  // Nom propi de la línia dins el Cronograma — independent de `description`.
  // Editar-lo NOMÉS afecta el Cronograma; mai "Línies del Expedient", que es
  // sempre la font de veritat del desglose. Si es buit, es mostra
  // `description`/`detail`/`referencia` com a referencia visual.
  cronogramaLabel?: string | null;
  // Id estable de l'ítem, generat pel backend la primera vegada que
  // s'importa al Cronograma (inCronograma: true) — enllaça la fase de
  // Planificació General sincronitzada amb aquest ítem concret, immune a
  // que l'índex es desplaci si s'elimina una línia anterior del desglose.
  // Mai es genera ni s'edita des del frontend.
  cronogramaItemId?: string | null;
}

interface BudgetLineOption {
  id: string;
  ordre?: number;
  concepte: string;
  descripcioLlarga?: string | null;
  unitat?: string;
  tipus?: string;
  llarg?: number;
  ample?: number;
  alt?: number;
  nodeId?: string | null;
  nodeCode?: string | null;
  nodeName?: string | null;
  totalHores: number;
  costMaterials: number;
  costServeis?: number;
  costIndirecte?: number;
  totalCost?: number;
  preuUA?: number;
  preuUT?: number;
  descompte?: number;
  factor?: number;
  import?: number;
  medicio?: number;
  amidament?: number;
  pes?: number;
  escandallLines?: EscandallDetailLine[];
}

interface Brand {
  id: string;
  code: string;
  name: string;
}

interface ClientOption {
  id: string;
  name: string;
}

interface CompositeCodeOption {
  id: number;
  code: string;
  name: string;
}

interface Project {
  id: string;
  expedientNumber: number;
  name: string;
  description?: string;
  brandId?: string;
  clientId?: string;
  productCode?: string;
  units: number;
  unitType: string;
  clientName?: string;
  brandName?: string;
  startDate?: string;
  expectedEndDate?: string;
  duration?: string;
  notes?: string;
  status: string;
  prevHours: number;
  realHours: number;
  estimatedRevenue: number;
  actualRevenue: number;
  estimatedMaterialCost: number;
  actualMaterialCost: number;
  estimatedLaborCost: number;
  actualLaborCost: number;
  estimatedServiceCost: number;
  actualServiceCost: number;
  budgetId?: string | null;
  createdFromBudget?: number;
  cronogramaChapterOrder?: string[] | null;
  cronogramaLines?: CronogramaLine[] | null;
}

// Línia 100% de planificació creada des del Cronograma (sense cap relació
// amb el detall/costos) — a diferencia dels capítols (que surten del
// desglose real), aquestes son purament per organitzar la línia temporal de
// treball. Cada una pot tenir diversos segments (períodes) independents.
interface CronogramaSegment {
  id: string;
  startDate: string;
  endDate: string;
}
interface CronogramaLine {
  id: string;
  name: string;
  segments: CronogramaSegment[];
  // Quan s'informa, aquesta línia de planificació (sense cost) es mostra
  // agrupada dins del capítol amb aquest nom — si coincideix amb un capítol
  // real del desglose, apareix com una fila mes dins el seu grup; si no
  // coincideix amb cap, forma el seu propi grup "virtual" (capítol creat
  // nomes per planificar, sense cap partida de cost al darrere).
  chapterTag?: string | null;
}

interface EmployeeOption {
  id: string;
  firstName: string;
  lastName: string;
}

interface WorkLogEntry {
  id: string;
  employeeId: string;
  employee?: { firstName: string; lastName: string };
  workOrderId?: string;
  workDate: string;
  hours: number;
  description?: string;
}

interface WorkOrderTypeOption {
  id: number;
  code: string;
  name: string;
  description?: string;
  sortOrder: number;
}

interface Props {
  // Absent = mode "Nou Expedient" (crea un projecte nou en aquesta mateixa
  // pantalla, en comptes del formulari separat que hi havia abans).
  projectId?: string;
  initialEdit?: boolean;
  onBack: () => void;
}

interface EditFormState {
  name: string;
  brandId: string;
  clientId: string;
  productCode: string;
  status: string;
  startDate: string;
  expectedEndDate: string;
  duration: string;
  units: number;
  unitType: string;
  prevHours: number;
  estimatedRevenue: number;
  estimatedMaterialCost: number;
  notes: string;
}

const EMPTY_EDIT_FORM: EditFormState = {
  name: '', brandId: '', clientId: '', productCode: '', status: 'ADJUDICAT',
  startDate: '', expectedEndDate: '', duration: '', units: 1, unitType: 'Ut',
  prevHours: 0, estimatedRevenue: 0, estimatedMaterialCost: 0, notes: '',
};

const formatNumber = (num: number | string | undefined | null): string => {
  // Els camps decimal venen de l'API com a string (mysql2/TypeORM) — sense
  // convertir a Number, Object.prototype.toLocaleString() ignora silenciosament
  // les opcions de locale i retorna el string tal qual (sense separadors).
  const n = Number(num);
  if (num === undefined || num === null || isNaN(n)) return '0,00';
  return n.toLocaleString('ca-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Input numèric amb format català en repòs (1.234,56) i edició en cru mentre
// té el focus — mateix patró que els camps editables de NodeForm.tsx/
// BudgetForm.tsx (si es formatés en cada tecla, el cursor saltaria i es
// perdria el focus en escriure la coma decimal).
function NumInput({
  value, decimals = 2, onCommit, disabled, className, style, placeholder,
}: {
  value: number | null | undefined;
  decimals?: number;
  onCommit: (n: number) => void;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const display = editing
    ? draft
    : (!value ? '' : Number(value).toLocaleString('ca-ES', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }));
  return (
    <Input
      type="text"
      inputMode="decimal"
      disabled={disabled}
      value={display}
      placeholder={placeholder ?? '0'}
      onFocus={() => { setEditing(true); setDraft(!value ? '' : String(value)); }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={(e) => {
        onCommit(parseFloat(e.target.value.replace(',', '.')) || 0);
        setEditing(false);
        setDraft('');
      }}
      className={className}
      style={style}
    />
  );
}

// Alguns endpoints (SELECT * en cru, sense passar per find()/QueryBuilder de
// TypeORM) no deserialitzen columnes JSON — arriben com a string en lloc
// d'array. Es normalitza aquí perquè la resta del codi pugui confiar que
// sempre és un array (o undefined).
const parseGroupedBudgetLines = (
  value: unknown
): { projectBudgetLineId: string; budgetLineIndex: number }[] | undefined => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
};

const formatDate = (dateStr: string | undefined | null): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('ca-ES');
};

// Normalitza una data de columna DATE a 'YYYY-MM-DD' per a <input type="date">, tant si arriba
// com a string (TypeORM find()) com a objecte Date (driver mariadb en consultes SQL en cru,
// p.ex. GET /taller/projects/:id/work-orders).
const toISODateOrUndefined = (value: unknown): string | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  const str = String(value);
  return str.slice(0, 10) || undefined;
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending:     { label: 'Pendent',  color: 'bg-yellow-100 text-yellow-800' },
  in_progress: { label: 'En curs',  color: 'bg-blue-100 text-blue-800' },
  completed:   { label: 'Finalitzat', color: 'bg-green-100 text-green-800' },
  on_hold:     { label: 'Pausat',   color: 'bg-gray-100 text-gray-600' },
  cancelled:   { label: 'Cancel·lat', color: 'bg-red-100 text-red-700' },
};

// Fases del Kanban de seguiment de projectes (2026-10-04) — mateixos 7 codis que
// ProjectStatus al backend (Project.ts) + CANCELLED, fora del flux normal.
const PROJECT_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  ADJUDICAT:         { label: "Adjudicat / Pendent d'iniciar", color: 'bg-yellow-100 text-yellow-800' },
  OFICINA_TECNICA:   { label: 'Oficina tècnica',               color: 'bg-blue-100 text-blue-800' },
  COMPRES:           { label: 'Compres',                       color: 'bg-amber-100 text-amber-800' },
  FABRICACIO_TALLER: { label: 'Fabricació taller',              color: 'bg-indigo-100 text-indigo-800' },
  PREPARAT_MUNTATGE: { label: 'Preparat per muntatge',          color: 'bg-cyan-100 text-cyan-800' },
  EN_MUNTATGE:       { label: 'En muntatge',                   color: 'bg-purple-100 text-purple-800' },
  TANCAMENT:         { label: 'Tancament',                     color: 'bg-green-100 text-green-800' },
  CANCELLED:         { label: 'Cancel·lat',                    color: 'bg-red-100 text-red-700' },
};

const euroFormatter = (v: number) =>
  v >= 1000 ? `${(v / 1000).toFixed(1)}k€` : `${v.toFixed(0)}€`;

interface StatsPanelProps {
  project: Project;
  workOrders: WorkOrder[];
  totalPrevHours: number;
  totalRealHours: number;
  totalInvoices: number;
  totalMaterialCost: number;
  totalServiceCost: number;
  totalLaborCost: number;
}

function StatsPanel({
  project, workOrders, totalPrevHours, totalRealHours,
  totalInvoices, totalMaterialCost, totalServiceCost, totalLaborCost,
}: StatsPanelProps) {
  const pctHores = totalPrevHours > 0
    ? Math.min(Math.round((totalRealHours / totalPrevHours) * 100), 999)
    : 0;
  const devHores = totalRealHours - totalPrevHours;
  const margeEstimat = (project.estimatedRevenue ?? 0)
    - (project.estimatedMaterialCost ?? 0)
    - (project.estimatedServiceCost ?? 0)
    - (project.estimatedLaborCost ?? 0);
  const margeReal = totalInvoices - totalMaterialCost - totalServiceCost - totalLaborCost;
  const margeColor = margeReal >= margeEstimat ? 'text-green-600' : 'text-red-600';

  const otsByStatus = workOrders.reduce<Record<string, number>>((acc, ot) => {
    const key = ot.status || 'pending';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const costChartData = [
    {
      name: 'Ingressos',
      Previsió: project.estimatedRevenue ?? 0,
      Real: totalInvoices,
    },
    {
      name: 'Materials',
      Previsió: project.estimatedMaterialCost ?? 0,
      Real: totalMaterialCost,
    },
    {
      name: 'Serveis',
      Previsió: project.estimatedServiceCost ?? 0,
      Real: totalServiceCost,
    },
    {
      name: 'Treball',
      Previsió: project.estimatedLaborCost ?? 0,
      Real: totalLaborCost,
    },
  ];

  const hoursChartData = [
    { name: 'Hores prev.', value: totalPrevHours, fill: '#93c5fd' },
    { name: 'Hores real', value: totalRealHours, fill: devHores > 0 ? '#f87171' : '#4ade80' },
  ];

  return (
    <>
      <div className="p-4 space-y-4">
        {/* KPIs */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-gray-50 rounded-lg p-2 text-center">
            <p className="text-xs text-gray-500 mb-0.5">% Hores</p>
            <p className={`text-sm font-bold ${pctHores > 100 ? 'text-red-600' : 'text-gray-800'}`}>
              {pctHores}%
            </p>
            <p className={`text-xs ${devHores > 0 ? 'text-red-500' : 'text-green-600'}`}>
              {devHores > 0 ? '+' : ''}{devHores.toFixed(1)}h
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2 text-center">
            <p className="text-xs text-gray-500 mb-0.5">Marge previst</p>
            <p className={`text-sm font-bold ${margeEstimat >= 0 ? 'text-gray-800' : 'text-red-600'}`}>
              {margeEstimat >= 0 ? '' : '-'}{Math.abs(margeEstimat).toLocaleString('ca-ES', { maximumFractionDigits: 0 })}€
            </p>
            <p className="text-xs text-gray-400">estimat</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2 text-center">
            <p className="text-xs text-gray-500 mb-0.5">Marge real</p>
            <p className={`text-sm font-bold ${margeColor}`}>
              {margeReal >= 0 ? '' : '-'}{Math.abs(margeReal).toLocaleString('ca-ES', { maximumFractionDigits: 0 })}€
            </p>
            <p className="text-xs text-gray-400">actual</p>
          </div>
        </div>

        {/* Gràfic costos */}
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            Previsió vs Real (€)
          </p>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={costChartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tickFormatter={euroFormatter} tick={{ fontSize: 10 }} width={42} />
              <Tooltip
                formatter={(value: number) =>
                  value.toLocaleString('ca-ES', { minimumFractionDigits: 2 }) + ' €'
                }
              />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="Previsió" fill="#93c5fd" radius={[2, 2, 0, 0]} />
              <Bar dataKey="Real" fill="#3b82f6" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Gràfic hores */}
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            Hores
          </p>
          <ResponsiveContainer width="100%" height={90}>
            <BarChart data={hoursChartData} layout="vertical" margin={{ top: 0, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={65} />
              <Tooltip formatter={(v: number) => `${v.toFixed(2)} h`} />
              <Bar dataKey="value" radius={[0, 2, 2, 0]}>
                {hoursChartData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* OTs per estat */}
        {workOrders.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              OTs per estat
            </p>
            <div className="flex flex-wrap gap-1">
              {Object.entries(otsByStatus).map(([status, count]) => {
                const cfg = STATUS_LABELS[status] ?? { label: status, color: 'bg-gray-100 text-gray-600' };
                return (
                  <span key={status} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
                    {count} {cfg.label}
                  </span>
                );
              })}
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                {workOrders.length} total
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default function ExpedientDetail({ projectId, initialEdit, onBack }: Props) {
  const navigate = useNavigate();
  const isNew = !projectId;
  const [project, setProject] = useState<Project | null>(null);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [loading, setLoading] = useState(!isNew);
  // Pestanyes (mateix patró que BudgetForm.tsx/NodeForm.tsx: Dades Generals / Detall) — "Línies
  // del pressupost" en pàgina a part perquè hi càpiguen totes les columnes del desglose.
  const [tab, setTab] = useState<'general' | 'detall' | 'cronograma'>('general');

  // Edició inline de les dades generals — mateixa pantalla per crear
  // ("Nou Expedient") i per editar, en comptes del formulari separat que
  // hi havia abans a Expedients.tsx. Sempre editable (2026-10-08, sense mode
  // "nomes lectura" separat, com Clients/Expedients Comercials/Pressupostos).
  const [editForm, setEditForm] = useState<EditFormState>(EMPTY_EDIT_FORM);
  const [savingEdit, setSavingEdit] = useState(false);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [compositeCodes, setCompositeCodes] = useState<CompositeCodeOption[]>([]);
  const [showOTModal, setShowOTModal] = useState(false);
  const [editingOT, setEditingOT] = useState<WorkOrder | null>(null);
  const [otFormData, setOtFormData] = useState<{
    name: string; typeId: number; nodeId: string; projectBudgetLineId: string;
    budgetLineIndex?: number; assignedToId: string; description: string;
    units: number; unitType: string; prevHours: number; prevMaterials: number;
    plannedStartDate: string; plannedEndDate: string;
  }>({
    name: '',
    typeId: 0,
    nodeId: '',
    projectBudgetLineId: '',
    budgetLineIndex: undefined,
    assignedToId: '',
    description: '',
    units: 1,
    unitType: 'Ut',
    prevHours: 0,
    prevMaterials: 0,
    plannedStartDate: '',
    plannedEndDate: '',
  });
  const [otTypes, setOtTypes] = useState<WorkOrderTypeOption[]>([]);
  const [escandalls, setEscandalls] = useState<EscandallOption[]>([]);
  const [budgetLines, setBudgetLines] = useState<BudgetLineOption[]>([]);
  // ── Cronograma ──────────────────────────────────────────────────────────
  // Dos tipus de files combinades a la mateixa vista: capítols (surten del
  // detall real, escandallLines) i línies 100% de planificació
  // (Project.cronogramaLines, sense cap relació amb el detall/costos).
  const [cronoRangeStart, setCronoRangeStart] = useState<string | null>(null);
  const [cronoSavingKey, setCronoSavingKey] = useState<string | null>(null);
  const [cronoResizing, setCronoResizing] = useState<{ lineId: string; index: number; segmentId: string; previewEnd: string } | null>(null);
  const [cronoMoving, setCronoMoving] = useState<{ lineId: string; index: number; segmentId: string; previewStart: string; previewEnd: string } | null>(null);
  const [cronoChapterOrder, setCronoChapterOrder] = useState<string[]>([]);
  const [cronoChapterNameDrafts, setCronoChapterNameDrafts] = useState<Record<string, string>>({});
  const [savingCronoChapter, setSavingCronoChapter] = useState<string | null>(null);
  const [cronoChapterDragFrom, setCronoChapterDragFrom] = useState<string | null>(null);
  const [cronoChapterDropTarget, setCronoChapterDropTarget] = useState<string | null>(null);
  const [cronoLines, setCronoLines] = useState<CronogramaLine[]>([]);
  const cronoLinesRef = useRef<CronogramaLine[]>([]);
  useEffect(() => { cronoLinesRef.current = cronoLines; }, [cronoLines]);

  // Selector "Importar línies al Cronograma" — el Cronograma comença buit;
  // aquí es trien, capítol a capítol, quines línies de mà d'obra/serveis es
  // volen planificar.
  const [showCronoImportPicker, setShowCronoImportPicker] = useState(false);
  const [cronoImportSelection, setCronoImportSelection] = useState<Set<string>>(new Set());
  const [cronoImporting, setCronoImporting] = useState(false);
  useEffect(() => {
    if (!project) return;
    setCronoChapterOrder(project.cronogramaChapterOrder ?? []);
    setCronoLines(project.cronogramaLines ?? []);
  }, [project?.id]);
  const [cronoLineSaving, setCronoLineSaving] = useState(false);
  const [addingCronoLine, setAddingCronoLine] = useState(false);
  const [customCronoMoving, setCustomCronoMoving] = useState<{ lineId: string; segmentId: string; previewStart: string; previewEnd: string } | null>(null);
  const [customCronoResizing, setCustomCronoResizing] = useState<{ lineId: string; segmentId: string; previewEnd: string } | null>(null);
  const [cronoLineDragFrom, setCronoLineDragFrom] = useState<string | null>(null);
  const [cronoLineDropTarget, setCronoLineDropTarget] = useState<string | null>(null);
  const [exportingCrono, setExportingCrono] = useState(false);
  // Unitats de mesura (Ut, ml, m2, h...) — mateix catàleg que el Detall de l'Escandall
  // (NodeForm.tsx), per al selector "UA" del desglose.
  const [unitTypes, setUnitTypes] = useState<{ code: string; name: string }[]>([]);
  const [expandedBudgetLines, setExpandedBudgetLines] = useState<Set<string>>(new Set());
  const toggleBudgetLine = (id: string) => {
    setExpandedBudgetLines((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Selecció de sublínies del desglose per a la creació en lot d'OT's — clau
  // "lineId::index", individual (checkbox per fila) o col·lectiva (checkbox per capítol).
  const [selectedSublines, setSelectedSublines] = useState<Set<string>>(new Set());
  const [creatingBulkOTs, setCreatingBulkOTs] = useState(false);
  const [reimportingBudgetLines, setReimportingBudgetLines] = useState(false);
  const sublineKey = (lineId: string, index: number) => `${lineId}::${index}`;
  const toggleSublineSelection = (lineId: string, index: number) => {
    const key = sublineKey(lineId, index);
    setSelectedSublines((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Edició de "Línies del pressupost" a l'Expedient de Taller (2026-10-05) — a diferencia del
  // pressupost (teòric), aquí es la realitat d'execució: es pot editar, afegir i eliminar línies
  // i el seu desglose com en un Escandall. Buffer local per línia (budgetLineDrafts) perquè els
  // canvis no es desen fins que l'usuari prem "Guardar" — evita una crida PUT per tecla.
  const [budgetLineDrafts, setBudgetLineDrafts] = useState<Record<string, BudgetLineOption>>({});
  // Buffer local del camp "Cap." (mateix patró que NodeForm.tsx) — escriure
  // directament sobre sub.chapter reagrupa la taula (groupByChapter) a cada
  // tecla i l'input perd el focus; només s'aplica el valor a onBlur.
  const [capitolDrafts, setCapitolDrafts] = useState<Record<string, string>>({});
  // Modal de cerca gran per a l'"Element" del desglose (mateix patró que el
  // popup "Vincular Escandall" de BudgetForm.tsx) — s'obre amb la icona ⬡
  // al costat del desplegable petit, per quan aquest es queda curt.
  const [elementPickerFor, setElementPickerFor] = useState<{ lineId: string; index: number } | null>(null);
  const [savingBudgetLineId, setSavingBudgetLineId] = useState<string | null>(null);
  const [addingBudgetLine, setAddingBudgetLine] = useState(false);

  // Reordenació per arrossegament de les línies de pressupost — mateix
  // mecanisme que BudgetForm.tsx/NodeForm.tsx (handleLineDragStart/Drop).
  const [dragLineFrom, setDragLineFrom] = useState<number | null>(null);
  const [dropLineTarget, setDropLineTarget] = useState<number | null>(null);
  const handleLineDragStart = (idx: number) => setDragLineFrom(idx);
  const handleLineDragEnd = () => { setDragLineFrom(null); setDropLineTarget(null); };
  const handleLineDrop = async (toIdx: number) => {
    const fromIdx = dragLineFrom;
    setDragLineFrom(null);
    setDropLineTarget(null);
    if (fromIdx === null || fromIdx === toIdx) return;
    const reordered = [...budgetLines];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    const withOrdre = reordered.map((l, i) => ({ ...l, ordre: i }));
    setBudgetLines(withOrdre);
    try {
      await Promise.all(withOrdre.map((l) => api.put(`/taller/projects/${projectId}/budget-lines/${l.id}`, { ordre: l.ordre })));
    } catch (error) {
      console.error('Error reordenant linies de pressupost:', error);
      fetchBudgetLines();
    }
  };

  // Reordenació per arrossegament de les files del desglose dins una mateixa
  // línia — bloquejada si origen o destí tenen OT's creades (mateixa
  // protecció que l'eliminació, per no trencar budgetLineIndex).
  const [dragSublineFrom, setDragSublineFrom] = useState<{ lineId: string; index: number } | null>(null);
  const [dropSublineTarget, setDropSublineTarget] = useState<string | null>(null);
  const handleSublineDragStart = (lineId: string, index: number) => setDragSublineFrom({ lineId, index });
  const handleSublineDragEnd = () => { setDragSublineFrom(null); setDropSublineTarget(null); };
  const handleSublineDrop = (line: BudgetLineOption, toIndex: number) => {
    const from = dragSublineFrom;
    setDragSublineFrom(null);
    setDropSublineTarget(null);
    if (!from || from.lineId !== line.id || from.index === toIndex) return;
    const current = effectiveBudgetLine(line);
    const subs = [...(current.escandallLines ?? [])];
    const [moved] = subs.splice(from.index, 1);
    subs.splice(toIndex, 0, moved);
    updateBudgetLineDraft(line, { escandallLines: subs, totalCost: recalcTotalCostFromDesglose(line, subs) });
  };

  const effectiveBudgetLine = (line: BudgetLineOption): BudgetLineOption => budgetLineDrafts[line.id] ?? line;

  const updateBudgetLineDraft = (line: BudgetLineOption, patch: Partial<BudgetLineOption>) => {
    setBudgetLineDrafts((prev) => ({ ...prev, [line.id]: { ...(prev[line.id] ?? line), ...patch } }));
  };

  // Recalcula el "Total cost" de la capçalera a partir del Totals (Cost
  // total) del desglose — mateix patró que BudgetForm.tsx
  // (recalcHeaderFromDetail). El "Preu" (preuUT) és un camp lliure que NO es
  // toca aquí; el "Preu/U" es deriva sempre com Preu/Qt en mostrar-lo.
  const recalcTotalCostFromDesglose = (line: BudgetLineOption, subs: EscandallDetailLine[]): number =>
    subs.reduce((s, sub) => s + computeSublineCalc(sub, line).costTotal, 0);

  // Aplica un material/escandall seleccionat (des del desplegable petit o del
  // modal gran ⬡) a una fila del desglose — compartit entre tots dos.
  const applyMaterialToSubline = (line: BudgetLineOption, index: number, currentNature: string | undefined, mat: MaterialSearchResult, nature: string | undefined) => {
    const isLabor = nature === 'MO_PROPIA' || nature === 'MO_SUBCONTRACT';
    const current = effectiveBudgetLine(line).escandallLines?.[index];
    const measurement = current ? escandallLineMeasurement(current) : 0;
    updateSublineDraft(line, index, {
      childType: 'MATERIAL',
      childMaterialId: mat.id,
      childNodeId: null,
      description: mat.name,
      nature: nature || currentNature || 'MATERIAL',
      unit: isLabor ? 'H' : mat.unit,
      resolvedUnitCost: Number(mat.pricePerMagnitude ?? mat.costPerUnit ?? 0),
      elementImageUrl: mat.technicalImageUrl ?? null,
      weightKgPerUnit: Number(mat.weightKgPerUnit ?? 0),
      pesTotal: Number(mat.weightKgPerUnit ?? 0) * measurement,
    });
  };

  const applyNodeToSubline = (line: BudgetLineOption, index: number, node: NodeSearchResult) => {
    const current = effectiveBudgetLine(line).escandallLines?.[index];
    const measurement = current ? escandallLineMeasurement(current) : 0;
    updateSublineDraft(line, index, {
      childType: 'NODE',
      childNodeId: node.id,
      childMaterialId: null,
      description: node.name,
      unit: node.unit,
      resolvedUnitCost: Number(node.cachedTotalDirectCost || 0),
      isPriceLocked: false,
      weightKgPerUnit: Number(node.cachedTotalWeightKg ?? 0),
      pesTotal: Number(node.cachedTotalWeightKg ?? 0) * measurement,
    });
  };

  // Camps que afecten la Medició (quantity * A * B * H) — en canviar-ne un,
  // recalcula pesTotal a partir del pes unitari del material/escandall
  // vinculat (weightKgPerUnit), igual que ja passa amb el Cost. Mateix patró
  // que updateDetailLine a BudgetForm.tsx.
  const SUBLINE_MEASUREMENT_FIELDS: (keyof EscandallDetailLine)[] = ['quantity', 'dimensionA', 'dimensionB', 'dimensionH'];

  const updateSublineDraft = (line: BudgetLineOption, index: number, patch: Partial<EscandallDetailLine>) => {
    const current = effectiveBudgetLine(line);
    const subs = [...(current.escandallLines ?? [])];
    const sub = { ...subs[index], ...patch };
    const patchedMeasurementField = Object.keys(patch).some((f) => SUBLINE_MEASUREMENT_FIELDS.includes(f as keyof EscandallDetailLine));
    if (patchedMeasurementField && Number(sub.weightKgPerUnit)) {
      sub.pesTotal = Number(sub.weightKgPerUnit) * escandallLineMeasurement(sub);
    }
    subs[index] = sub;
    updateBudgetLineDraft(line, { escandallLines: subs, totalCost: recalcTotalCostFromDesglose(line, subs) });
  };

  const addSublineDraft = (line: BudgetLineOption, chapter?: string) => {
    const current = effectiveBudgetLine(line);
    const subs = [
      ...(current.escandallLines ?? []),
      { chapter: chapter || '', childType: 'MATERIAL', nature: 'MATERIAL', description: 'Nova línia', quantity: 1 } as EscandallDetailLine,
    ];
    updateBudgetLineDraft(line, { escandallLines: subs, totalCost: recalcTotalCostFromDesglose(line, subs) });
  };

  const removeSublineDraft = (line: BudgetLineOption, index: number) => {
    const current = effectiveBudgetLine(line);
    const subs = (current.escandallLines ?? []).filter((_, i) => i !== index);
    updateBudgetLineDraft(line, { escandallLines: subs, totalCost: recalcTotalCostFromDesglose(line, subs) });
  };

  const handleCancelBudgetLineDraft = (lineId: string) => {
    setBudgetLineDrafts((prev) => {
      const next = { ...prev };
      delete next[lineId];
      return next;
    });
  };

  const handleSaveBudgetLine = async (line: BudgetLineOption) => {
    const draft = budgetLineDrafts[line.id];
    if (!draft) return;
    setSavingBudgetLineId(line.id);
    try {
      const response = await api.put<{ success: boolean; data: BudgetLineOption }>(
        `/taller/projects/${projectId}/budget-lines/${line.id}`,
        {
          concepte: draft.concepte,
          unitat: draft.unitat,
          tipus: draft.tipus,
          medicio: draft.medicio,
          preuUT: draft.preuUT,
          totalCost: recalcTotalCostFromDesglose(line, draft.escandallLines ?? []),
          escandallLines: draft.escandallLines,
        }
      );
      setBudgetLines((prev) => prev.map((l) => (l.id === line.id ? { ...l, ...response.data } : l)));
      handleCancelBudgetLineDraft(line.id);
    } catch (error: any) {
      console.error('Error desant linia de pressupost:', error);
      alert(error?.response?.data?.error || 'Error en desar la línia. Torna-ho a intentar.');
    } finally {
      setSavingBudgetLineId(null);
    }
  };

  // ── Cronograma ──────────────────────────────────────────────────────────
  // Capítols = agrupació per EscandallDetailLine.chapter a traves de TOTES
  // les línies de pressupost del projecte (mateix camp/utilitat que ja
  // s'usa a la pestanya "Detall" — no es una entitat nova). Cada item
  // conserva la referencia {lineId, index} per poder-se desar individualment.
  interface CronoItem extends EscandallDetailLine {
    __lineId: string;
    __index: number;
  }
  const buildCronoChapters = () => {
    const flat: CronoItem[] = [];
    budgetLines.forEach((line) => {
      (line.escandallLines ?? []).forEach((item, index) => {
        // El Cronograma comença buit en crear l'expedient: nomes hi apareixen
        // les línies que l'usuari ha importat explícitament (inCronograma).
        // Qualsevol línia es pot importar, no nomes mà d'obra/serveis — de
        // vegades un material tambe val la pena planificar-lo en el temps.
        if (!item.inCronograma) return;
        flat.push({ ...item, __lineId: line.id, __index: index });
      });
    });
    const groups = groupByChapter(flat);
    // Ordre: primer el que l'usuari hagi fixat arrossegant (cronoChapterOrder),
    // despres la resta per ordre natural d'aparició, i "Sense capítol" sempre
    // al final.
    return [...groups].sort((a, b) => {
      if (a.chapter === SENSE_CHAPTER) return 1;
      if (b.chapter === SENSE_CHAPTER) return -1;
      const ia = cronoChapterOrder.indexOf(a.chapter);
      const ib = cronoChapterOrder.indexOf(b.chapter);
      if (ia === -1 && ib === -1) return 0;
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  };

  // TOTES les línies del desglose encara NO importades al Cronograma —
  // candidates pel selector "Importar línies" (sense restringir per
  // naturalesa: de vegades un material tambe val la pena planificar-lo).
  // Agrupades per capítol, mateix patró que buildCronoChapters.
  const buildCronoImportCandidates = () => {
    const flat: CronoItem[] = [];
    budgetLines.forEach((line) => {
      (line.escandallLines ?? []).forEach((item, index) => {
        if (item.inCronograma) return;
        flat.push({ ...item, __lineId: line.id, __index: index });
      });
    });
    return groupByChapter(flat);
  };

  // Periodes de planificació efectius d'un ítem del desglose — llegeix el
  // format nou (plannedSegments, múltiples periodes independents) i, si no
  // n'hi ha, cau al format antic (un sol plannedStartDate/plannedEndDate,
  // dades de pressupostos anteriors a 2026-10-19).
  const cronoItemSegments = (item: EscandallDetailLine): { id: string; startDate: string; endDate: string }[] => {
    if (item.plannedSegments && item.plannedSegments.length > 0) return item.plannedSegments;
    if (item.plannedStartDate && item.plannedEndDate) {
      return [{ id: 'legacy', startDate: item.plannedStartDate, endDate: item.plannedEndDate }];
    }
    return [];
  };

  const saveCronoItemPatch = async (lineId: string, index: number, patch: Record<string, unknown>) => {
    const line = budgetLines.find((l) => l.id === lineId);
    if (!line) return;
    const key = `${lineId}:${index}`;
    setCronoSavingKey(key);
    try {
      // Endpoint dedicat — nomes toca escandallLines[index], no passa per la
      // protecció "ja te OT's creades" (aquella es per canvis de cost/
      // estructura; planificar dates d'un ítem amb OT ja creada es el cas
      // d'us normal del Cronograma). Es genèric (merge directe del patch),
      // per aixo pot rebre tant el format antic com plannedSegments.
      const response = await api.patch<{ success: boolean; data: BudgetLineOption }>(
        `/taller/projects/${projectId}/budget-lines/${lineId}/planning/${index}`,
        patch,
      );
      setBudgetLines((prev) => prev.map((l) => (l.id === lineId ? { ...l, ...response.data } : l)));
    } catch (error: any) {
      console.error('Error desant planificació del cronograma:', error);
      alert(error?.response?.data?.error || 'Error en desar la planificació. Torna-ho a intentar.');
    } finally {
      setCronoSavingKey(null);
    }
  };

  const saveCronoItemSegments = (lineId: string, index: number, segments: { id: string; startDate: string; endDate: string }[]) =>
    saveCronoItemPatch(lineId, index, { plannedSegments: segments, plannedStartDate: null, plannedEndDate: null });

  const handleAddCronoItemSegment = (lineId: string, index: number, item: EscandallDetailLine) => {
    const effectiveRangeStart = cronoRangeStart || project?.startDate || toISO(new Date());
    const segments = [...cronoItemSegments(item), { id: crypto.randomUUID(), startDate: effectiveRangeStart, endDate: effectiveRangeStart }];
    saveCronoItemSegments(lineId, index, segments);
  };

  const handleDeleteCronoItemSegment = (lineId: string, index: number, item: EscandallDetailLine, segmentId: string) => {
    saveCronoItemSegments(lineId, index, cronoItemSegments(item).filter((s) => s.id !== segmentId));
  };

  // Treu una línia del Cronograma — el Cronograma nomes planifica temps, no
  // te res a veure amb el desglose de costos: aixo nomes desmarca
  // inCronograma i buida la seva planificació (dates/segments). La partida
  // de cost NO es toca ni s'elimina; es pot tornar a importar quan calgui.
  const handleRemoveFromCronograma = async (lineId: string, index: number) => {
    if (!confirm('Treure aquesta línia del Cronograma? La partida de cost del pressupost no es modifica, nomes deixa d\'estar planificada.')) return;
    await saveCronoItemPatch(lineId, index, { inCronograma: false, plannedStartDate: null, plannedEndDate: null, plannedSegments: [] });
  };

  // Importa al Cronograma totes les línies marcades al selector — nomes
  // marca inCronograma, no toca cap altre camp.
  const handleImportCronoSelection = async () => {
    if (cronoImportSelection.size === 0) { setShowCronoImportPicker(false); return; }
    setCronoImporting(true);
    try {
      // Sequencial, NO Promise.all: diverses línies seleccionades solen
      // compartir el mateix lineId (mateixa línia de pressupost, diferent
      // índex del desglose). Cada PATCH llegeix i reescriu la fila sencera
      // (tot l'array escandallLines), així que dos PATCH concurrents sobre
      // el mateix lineId es trepitgen entre si i la segona escriptura
      // esborra la primera (lost update) — per aixo calia fer-ho d'un en un.
      for (const key of cronoImportSelection) {
        const [lineId, indexStr] = key.split(':');
        await saveCronoItemPatch(lineId, parseInt(indexStr, 10), { inCronograma: true });
      }
      setCronoImportSelection(new Set());
      setShowCronoImportPicker(false);
    } finally {
      setCronoImporting(false);
    }
  };

  // Reanomena una línia NOMÉS dins el Cronograma (camp cronogramaLabel,
  // independent de `description`) — s'actualitza localment a cada tecla
  // (perque l'input no perdi el focus en re-renderitzar) i es desa nomes en
  // perdre el focus. Mai toca "Línies del Expedient".
  const updateCronoItemLabelLocal = (lineId: string, index: number, cronogramaLabel: string) => {
    setBudgetLines((prev) => prev.map((l) => {
      if (l.id !== lineId) return l;
      const lines = [...(l.escandallLines ?? [])];
      lines[index] = { ...lines[index], cronogramaLabel };
      return { ...l, escandallLines: lines };
    }));
  };
  const handleCronoItemLabelBlur = (lineId: string, index: number, cronogramaLabel: string) => {
    saveCronoItemPatch(lineId, index, { cronogramaLabel });
  };

  const applyCronoItemSegmentPatch = (lineId: string, index: number, item: EscandallDetailLine, segmentId: string, patch: { startDate?: string; endDate?: string }) => {
    saveCronoItemSegments(lineId, index, cronoItemSegments(item).map((s) => (s.id === segmentId ? { ...s, ...patch } : s)));
  };

  const cronoDragStartX = useRef(0);

  const handleCronoMoveMouseDown = (e: React.MouseEvent, lineId: string, index: number, item: EscandallDetailLine, segmentId: string, start: string, end: string) => {
    e.preventDefault();
    e.stopPropagation();
    const rowEl = (e.currentTarget as HTMLElement).parentElement as HTMLElement | null;
    const dayPx = (rowEl?.getBoundingClientRect().width || 1) / CRONO_VISIBLE_DAYS;
    cronoDragStartX.current = e.clientX;
    setCronoMoving({ lineId, index, segmentId, previewStart: start, previewEnd: end });
    const duration = diffDaysISO(start, end);

    const onMove = (ev: MouseEvent) => {
      const deltaDays = Math.round((ev.clientX - cronoDragStartX.current) / dayPx);
      const newStart = addDaysISO(start, deltaDays);
      setCronoMoving({ lineId, index, segmentId, previewStart: newStart, previewEnd: addDaysISO(newStart, duration) });
    };
    const onUp = (ev: MouseEvent) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const deltaDays = Math.round((ev.clientX - cronoDragStartX.current) / dayPx);
      const newStart = addDaysISO(start, deltaDays);
      const newEnd = addDaysISO(newStart, duration);
      setCronoMoving(null);
      if (newStart !== start) applyCronoItemSegmentPatch(lineId, index, item, segmentId, { startDate: newStart, endDate: newEnd });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleCronoResizeMouseDown = (e: React.MouseEvent, lineId: string, index: number, item: EscandallDetailLine, segmentId: string, start: string, end: string) => {
    e.preventDefault();
    e.stopPropagation();
    const rowEl = (e.currentTarget as HTMLElement).parentElement?.parentElement as HTMLElement | null;
    const dayPx = (rowEl?.getBoundingClientRect().width || 1) / CRONO_VISIBLE_DAYS;
    cronoDragStartX.current = e.clientX;
    setCronoResizing({ lineId, index, segmentId, previewEnd: end });

    const onMove = (ev: MouseEvent) => {
      const deltaDays = Math.round((ev.clientX - cronoDragStartX.current) / dayPx);
      let newEnd = addDaysISO(end, deltaDays);
      if (newEnd < start) newEnd = start;
      setCronoResizing({ lineId, index, segmentId, previewEnd: newEnd });
    };
    const onUp = (ev: MouseEvent) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const deltaDays = Math.round((ev.clientX - cronoDragStartX.current) / dayPx);
      let newEnd = addDaysISO(end, deltaDays);
      if (newEnd < start) newEnd = start;
      setCronoResizing(null);
      if (newEnd !== end) applyCronoItemSegmentPatch(lineId, index, item, segmentId, { endDate: newEnd });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Reanomena un capítol — actualitza el camp `chapter` a TOTES les seves
  // línies del desglose (mateix camp que al Detall, no es una entitat nova).
  const handleRenameCronoChapter = async (oldName: string, newName: string) => {
    if (!newName.trim() || newName === oldName) return;
    setSavingCronoChapter(oldName);
    try {
      await api.patch(`/taller/projects/${projectId}/cronograma-chapters/rename`, { oldName, newName: newName.trim() });
      setCronoChapterOrder((prev) => prev.map((c) => (c === oldName ? newName.trim() : c)));
      await fetchBudgetLines();
    } catch (error) {
      console.error('Error reanomenant el capítol:', error);
      alert('Error en reanomenar el capítol. Torna-ho a intentar.');
    } finally {
      setSavingCronoChapter(null);
    }
  };

  // "Elimina" un capítol del Cronograma — nomes buida les dates planificades
  // de les seves línies; les partides de cost del detall NO es toquen.
  const handleClearCronoChapter = async (chapter: string) => {
    if (!confirm(`Treure la planificació de "${chapter === SENSE_CHAPTER ? 'Sense capítol' : chapter}"? Les partides del desglose no s'eliminaran, nomes les seves dates.`)) return;
    setSavingCronoChapter(chapter);
    try {
      await api.patch(`/taller/projects/${projectId}/cronograma-chapters/clear`, { chapter: chapter === SENSE_CHAPTER ? '' : chapter });
      await fetchBudgetLines();
    } catch (error) {
      console.error('Error buidant la planificació del capítol:', error);
      alert('Error en treure la planificació. Torna-ho a intentar.');
    } finally {
      setSavingCronoChapter(null);
    }
  };

  const saveCronoChapterOrder = async (order: string[]) => {
    try {
      await api.put(`/taller/projects/${projectId}/cronograma-chapter-order`, { order });
    } catch (error) {
      console.error('Error desant l\'ordre dels capítols:', error);
    }
  };

  const handleCronoChapterDrop = (targetChapter: string) => {
    const dragChapter = cronoChapterDragFrom;
    setCronoChapterDragFrom(null);
    setCronoChapterDropTarget(null);
    if (!dragChapter || dragChapter === targetChapter || targetChapter === SENSE_CHAPTER) return;
    const currentNames = buildCronoChapters().map((g) => g.chapter).filter((c) => c !== SENSE_CHAPTER);
    const reordered = [...currentNames];
    const from = reordered.indexOf(dragChapter);
    const to = reordered.indexOf(targetChapter);
    if (from === -1 || to === -1) return;
    reordered.splice(to, 0, reordered.splice(from, 1)[0]);
    setCronoChapterOrder(reordered);
    saveCronoChapterOrder(reordered);
  };

  // ── Cronograma: línies 100% de planificació (sense cap relació amb el
  // detall/costos) — vegeu CronogramaLine més amunt.
  const saveCronoLines = async (lines: CronogramaLine[]) => {
    setCronoLineSaving(true);
    try {
      await api.put(`/taller/projects/${projectId}/cronograma-lines`, { lines });
    } catch (error) {
      console.error('Error desant les línies de planificació:', error);
      alert('Error en desar el cronograma. Torna-ho a intentar.');
    } finally {
      setCronoLineSaving(false);
    }
  };

  const updateCronoLines = (updater: (prev: CronogramaLine[]) => CronogramaLine[]) => {
    setCronoLines((prev) => {
      const next = updater(prev);
      saveCronoLines(next);
      return next;
    });
  };

  // chapterTag opcional: si s'informa, la nova línia s'agrupa dins d'aquest
  // capítol (real o virtual) en lloc d'anar a "Línies de planificació".
  const handleAddCronoLine = async (chapterTag?: string, name = 'Nova línia') => {
    setAddingCronoLine(true);
    try {
      const newLine: CronogramaLine = { id: crypto.randomUUID(), name, segments: [], chapterTag: chapterTag || null };
      updateCronoLines((prev) => [...prev, newLine]);
    } finally {
      setAddingCronoLine(false);
    }
  };

  const handleDeleteCronoLine = (lineId: string) => {
    if (!confirm('Eliminar aquesta línia de planificació?')) return;
    updateCronoLines((prev) => prev.filter((l) => l.id !== lineId));
  };

  // Reanomena un capítol "virtual" (un chapterTag que no existeix al desglose
  // real) — actualitza el tag a TOTES les línies que el comparteixen.
  const handleRenameCronoChapterTag = (oldTag: string, newTag: string) => {
    if (!newTag.trim() || newTag === oldTag) return;
    updateCronoLines((prev) => prev.map((l) => (l.chapterTag === oldTag ? { ...l, chapterTag: newTag.trim() } : l)));
  };

  const [newChapterTagDraft, setNewChapterTagDraft] = useState(false);
  const handleAddNewChapterTag = (name: string) => {
    if (!name.trim()) { setNewChapterTagDraft(false); return; }
    handleAddCronoLine(name.trim(), 'Nova línia');
    setNewChapterTagDraft(false);
  };

  const handleRenameCronoLineChange = (lineId: string, name: string) => {
    setCronoLines((prev) => prev.map((l) => (l.id === lineId ? { ...l, name } : l)));
  };
  const handleRenameCronoLineBlur = () => {
    saveCronoLines(cronoLinesRef.current);
  };

  const handleAddCronoSegment = (lineId: string) => {
    const effectiveRangeStart = cronoRangeStart || project?.startDate || toISO(new Date());
    updateCronoLines((prev) => prev.map((l) => (l.id === lineId
      ? { ...l, segments: [...l.segments, { id: crypto.randomUUID(), startDate: effectiveRangeStart, endDate: effectiveRangeStart }] }
      : l)));
  };

  const handleDeleteCronoSegment = (lineId: string, segmentId: string) => {
    updateCronoLines((prev) => prev.map((l) => (l.id === lineId
      ? { ...l, segments: l.segments.filter((s) => s.id !== segmentId) }
      : l)));
  };

  const applyCronoSegmentPatch = (lineId: string, segmentId: string, patch: Partial<CronogramaSegment>) => {
    updateCronoLines((prev) => prev.map((l) => (l.id === lineId
      ? { ...l, segments: l.segments.map((s) => (s.id === segmentId ? { ...s, ...patch } : s)) }
      : l)));
  };

  // Per id (no per índex): les línies es renderitzen en subllistes filtrades
  // (per capítol/virtual/sense tag), aixi que l'índex local de cada llista no
  // es correspon amb la posició a l'array complet cronoLines.
  const handleCronoLineDragStart = (lineId: string) => setCronoLineDragFrom(lineId);
  const handleCronoLineDragEnd = () => { setCronoLineDragFrom(null); setCronoLineDropTarget(null); };
  const handleCronoLineDrop = (targetId: string) => {
    const fromId = cronoLineDragFrom;
    setCronoLineDragFrom(null);
    setCronoLineDropTarget(null);
    if (!fromId || fromId === targetId) return;
    updateCronoLines((prev) => {
      const reordered = [...prev];
      const fromIdx = reordered.findIndex((l) => l.id === fromId);
      const toIdx = reordered.findIndex((l) => l.id === targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      reordered.splice(toIdx, 0, reordered.splice(fromIdx, 1)[0]);
      return reordered;
    });
  };

  const customCronoDragStartX = useRef(0);

  const handleCustomCronoMoveMouseDown = (e: React.MouseEvent, lineId: string, segmentId: string, start: string, end: string) => {
    e.preventDefault();
    e.stopPropagation();
    const rowEl = (e.currentTarget as HTMLElement).parentElement as HTMLElement | null;
    const dayPx = (rowEl?.getBoundingClientRect().width || 1) / CRONO_VISIBLE_DAYS;
    customCronoDragStartX.current = e.clientX;
    setCustomCronoMoving({ lineId, segmentId, previewStart: start, previewEnd: end });
    const duration = diffDaysISO(start, end);

    const onMove = (ev: MouseEvent) => {
      const deltaDays = Math.round((ev.clientX - customCronoDragStartX.current) / dayPx);
      const newStart = addDaysISO(start, deltaDays);
      setCustomCronoMoving({ lineId, segmentId, previewStart: newStart, previewEnd: addDaysISO(newStart, duration) });
    };
    const onUp = (ev: MouseEvent) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const deltaDays = Math.round((ev.clientX - customCronoDragStartX.current) / dayPx);
      const newStart = addDaysISO(start, deltaDays);
      const newEnd = addDaysISO(newStart, duration);
      setCustomCronoMoving(null);
      if (newStart !== start) applyCronoSegmentPatch(lineId, segmentId, { startDate: newStart, endDate: newEnd });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleCustomCronoResizeMouseDown = (e: React.MouseEvent, lineId: string, segmentId: string, start: string, end: string) => {
    e.preventDefault();
    e.stopPropagation();
    const rowEl = (e.currentTarget as HTMLElement).parentElement?.parentElement as HTMLElement | null;
    const dayPx = (rowEl?.getBoundingClientRect().width || 1) / CRONO_VISIBLE_DAYS;
    customCronoDragStartX.current = e.clientX;
    setCustomCronoResizing({ lineId, segmentId, previewEnd: end });

    const onMove = (ev: MouseEvent) => {
      const deltaDays = Math.round((ev.clientX - customCronoDragStartX.current) / dayPx);
      let newEnd = addDaysISO(end, deltaDays);
      if (newEnd < start) newEnd = start;
      setCustomCronoResizing({ lineId, segmentId, previewEnd: newEnd });
    };
    const onUp = (ev: MouseEvent) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const deltaDays = Math.round((ev.clientX - customCronoDragStartX.current) / dayPx);
      let newEnd = addDaysISO(end, deltaDays);
      if (newEnd < start) newEnd = start;
      setCustomCronoResizing(null);
      if (newEnd !== end) applyCronoSegmentPatch(lineId, segmentId, { endDate: newEnd });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Exportació a Excel del Cronograma — rèplica visual de la graella que es
  // veu en pantalla (una columna per dia, capítols/línies com a files amb
  // les seves barres acolorides), no un llistat pla de dates.
  const handleExportCronograma = async () => {
    setExportingCrono(true);
    try {
      const { default: ExcelJS } = await import('exceljs');
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Cronograma');

      const chapterGroups = buildCronoChapters();
      const realChapterNames = new Set(chapterGroups.map((g) => g.chapter));
      const taggedByChapter = (chapter: string) => cronoLines.filter((l) => l.chapterTag === chapter);
      const untaggedLines = cronoLines.filter((l) => !l.chapterTag);
      const virtualTags: string[] = [];
      cronoLines.forEach((l) => {
        if (l.chapterTag && !realChapterNames.has(l.chapterTag) && !virtualTags.includes(l.chapterTag)) virtualTags.push(l.chapterTag);
      });

      // Rang complet a exportar: de la primera a la darrera data planificada
      // (no nomes les CRONO_VISIBLE_DAYS visibles en pantalla).
      const allDates: string[] = [];
      chapterGroups.forEach((g) => g.items.forEach(({ item }) => cronoItemSegments(item).forEach((s) => allDates.push(s.startDate, s.endDate))));
      cronoLines.forEach((l) => l.segments.forEach((s) => allDates.push(s.startDate, s.endDate)));
      const todayIso = toISO(new Date());
      const rangeStart = allDates.length ? [...allDates].sort()[0] : (project?.startDate || todayIso);
      const rangeEnd = allDates.length ? [...allDates].sort().slice(-1)[0] : addDaysISO(rangeStart, 30);
      const totalDays = diffDaysISO(rangeStart, rangeEnd) + 1;
      const FIXED_COLS = 3; // Pos / Descripció / Dies

      sheet.getColumn(1).width = 8;
      sheet.getColumn(2).width = 48;
      sheet.getColumn(3).width = 7;
      for (let i = 0; i < totalDays; i++) sheet.getColumn(FIXED_COLS + 1 + i).width = 2.6;

      // Capçalera: fila 1 = mesos (cel·les fusionades), fila 2 = dia del mes.
      sheet.mergeCells(1, 1, 2, 1);
      sheet.mergeCells(1, 2, 2, 2);
      sheet.mergeCells(1, 3, 2, 3);
      sheet.getCell(1, 1).value = 'Pos';
      sheet.getCell(1, 2).value = 'Descripció';
      sheet.getCell(1, 3).value = 'Dies';
      for (let c = 1; c <= FIXED_COLS; c++) {
        const cell = sheet.getCell(1, c);
        cell.font = { bold: true };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };
      }

      let monthStartCol = FIXED_COLS + 1;
      let prevLabel = '';
      for (let i = 0; i < totalDays; i++) {
        const dayIso = addDaysISO(rangeStart, i);
        const d = new Date(dayIso + 'T00:00:00');
        const label = `${CRONO_MONTH_LABELS[d.getMonth()]} ${d.getFullYear()}`;
        const col = FIXED_COLS + 1 + i;
        if (label !== prevLabel) {
          if (prevLabel) sheet.mergeCells(1, monthStartCol, 1, col - 1);
          monthStartCol = col;
          prevLabel = label;
        }
        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
        const isToday = dayIso === todayIso;
        const dayCell = sheet.getCell(2, col);
        dayCell.value = d.getDate();
        dayCell.alignment = { horizontal: 'center' };
        dayCell.font = { size: 8, bold: isToday };
        dayCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isToday ? 'FFFDE68A' : isWeekend ? 'FFF3F4F6' : 'FFFFFFFF' } };
        sheet.getCell(1, col).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isToday ? 'FFFDE68A' : 'FFE2E8F0' } };
      }
      if (prevLabel) sheet.mergeCells(1, monthStartCol, 1, FIXED_COLS + totalDays);
      for (let c = FIXED_COLS + 1; c <= FIXED_COLS + totalDays; c++) {
        const cell = sheet.getCell(1, c);
        if (cell.value) { cell.font = { bold: true, size: 9 }; cell.alignment = { horizontal: 'center', vertical: 'middle' }; }
      }

      const fillRangeInRow = (rowNumber: number, segStart: string, segEnd: string, argb: string) => {
        const startCol = Math.max(FIXED_COLS + 1, FIXED_COLS + 1 + diffDaysISO(rangeStart, segStart));
        const endCol = Math.min(FIXED_COLS + totalDays, FIXED_COLS + 1 + diffDaysISO(rangeStart, segEnd));
        for (let c = startCol; c <= endCol; c++) {
          sheet.getCell(rowNumber, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
        }
      };

      const CHAPTER_ARGB = ['FFE2E8F0', 'FFD9F99D', 'FFFED7AA', 'FFBAE6FD', 'FFD6D3D1', 'FFA7F3D0', 'FFFECACA', 'FF99F6E4', 'FFDDD6FE', 'FFFDE68A'];
      const ITEM_ARGB = 'FF14B8A6';
      const LINE_ARGB = 'FF6366F1';

      let rowNum = 3;
      const addGroupHeaderRow = (label: string, argb: string, range: { start: string; end: string } | null, groupIndex: number) => {
        const row = sheet.getRow(rowNum);
        row.getCell(1).value = `${groupIndex}.`;
        row.getCell(2).value = label;
        row.getCell(1).font = { bold: true };
        row.getCell(2).font = { bold: true };
        for (let c = 1; c <= FIXED_COLS; c++) row.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
        if (range) fillRangeInRow(rowNum, range.start, range.end, argb);
        rowNum++;
      };
      const addItemRow = (pos: string, label: string, segs: { startDate: string; endDate: string }[]) => {
        const row = sheet.getRow(rowNum);
        row.getCell(1).value = pos;
        row.getCell(2).value = label;
        const days = segs.reduce((s, sg) => s + diffDaysISO(sg.startDate, sg.endDate) + 1, 0);
        if (days) row.getCell(3).value = days;
        segs.forEach((seg) => fillRangeInRow(rowNum, seg.startDate, seg.endDate, ITEM_ARGB));
        rowNum++;
      };
      const addLineRow = (label: string, segs: { startDate: string; endDate: string }[]) => {
        const row = sheet.getRow(rowNum);
        row.getCell(2).value = `● ${label}`;
        row.getCell(2).font = { italic: true };
        const days = segs.reduce((s, sg) => s + diffDaysISO(sg.startDate, sg.endDate) + 1, 0);
        if (days) row.getCell(3).value = days;
        segs.forEach((seg) => fillRangeInRow(rowNum, seg.startDate, seg.endDate, LINE_ARGB));
        rowNum++;
      };

      chapterGroups.forEach((group, gi) => {
        const label = group.chapter === SENSE_CHAPTER ? 'Sense capítol' : group.chapter;
        const segs = group.items.flatMap(({ item }) => cronoItemSegments(item));
        const range = segs.length ? { start: [...segs.map((s) => s.startDate)].sort()[0], end: [...segs.map((s) => s.endDate)].sort().slice(-1)[0] } : null;
        addGroupHeaderRow(label, CHAPTER_ARGB[gi % CHAPTER_ARGB.length], range, gi + 1);
        group.items.forEach(({ item }, ii) => {
          addItemRow(`${gi + 1}.${String(ii + 1).padStart(2, '0')}`, item.cronogramaLabel || item.description || item.detail || item.referencia || '—', cronoItemSegments(item));
        });
        taggedByChapter(group.chapter).forEach((line) => addLineRow(line.name, line.segments));
      });

      virtualTags.forEach((tag, vi) => {
        addGroupHeaderRow(tag, CHAPTER_ARGB[(chapterGroups.length + vi) % CHAPTER_ARGB.length], null, chapterGroups.length + vi + 1);
        taggedByChapter(tag).forEach((line) => addLineRow(line.name, line.segments));
      });

      if (untaggedLines.length) {
        const row = sheet.getRow(rowNum);
        row.getCell(2).value = 'Línies de planificació';
        row.getCell(2).font = { bold: true, italic: true };
        rowNum++;
        untaggedLines.forEach((line) => addLineRow(line.name, line.segments));
      }

      sheet.views = [{ state: 'frozen', xSplit: FIXED_COLS, ySplit: 2 }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sanitizeFilename(`cronograma-${project?.expedientNumber ?? ''}`)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Error en exportar el cronograma a Excel');
    } finally {
      setExportingCrono(false);
    }
  };

  const handleAddBudgetLine = async () => {
    setAddingBudgetLine(true);
    try {
      const response = await api.post<{ success: boolean; data: BudgetLineOption }>(
        `/taller/projects/${projectId}/budget-lines`,
        { concepte: 'Nova línia', medicio: 1, preuUT: 0 }
      );
      setBudgetLines((prev) => [...prev, response.data]);
      setExpandedBudgetLines((prev) => new Set(prev).add(response.data.id));
    } catch (error) {
      console.error('Error afegint linia de pressupost:', error);
      alert('Error en afegir la línia. Torna-ho a intentar.');
    } finally {
      setAddingBudgetLine(false);
    }
  };

  const handleDeleteBudgetLine = async (line: BudgetLineOption) => {
    if (!confirm(`Eliminar la línia "${line.concepte}"? Aquesta acció no es pot desfer.`)) return;
    try {
      await api.delete(`/taller/projects/${projectId}/budget-lines/${line.id}`);
      setBudgetLines((prev) => prev.filter((l) => l.id !== line.id));
      handleCancelBudgetLineDraft(line.id);
    } catch (error: any) {
      console.error('Error eliminant linia de pressupost:', error);
      alert(error?.response?.data?.error || 'Error en eliminar la línia.');
    }
  };

  // Detall de línies del pressupost/escandall que formen cada OT — les OT's
  // automàtiques (Materials/Serveis/Fabricació/Muntatge) agreguen línies de
  // tot el pressupost, aixi que cal demanar-ho al backend en obrir la OT
  // (GET /work-orders/:id/detail-lines) en lloc de calcular-ho localment.
  const [expandedOTIds, setExpandedOTIds] = useState<Set<string>>(new Set());
  const [otDetailLines, setOtDetailLines] = useState<Record<string, (EscandallDetailLine & { __budgetLineConcepte?: string; __lineQty?: number })[]>>({});
  const [otDetailLoading, setOtDetailLoading] = useState<Set<string>>(new Set());
  const toggleOTDetail = async (ot: WorkOrder) => {
    setExpandedOTIds((prev) => {
      const next = new Set(prev);
      if (next.has(ot.id)) next.delete(ot.id); else next.add(ot.id);
      return next;
    });
    if (otDetailLines[ot.id]) return;
    setOtDetailLoading((prev) => new Set(prev).add(ot.id));
    try {
      const res = await api.get<{ success: boolean; data: (EscandallDetailLine & { __budgetLineConcepte?: string; __lineQty?: number })[] }>(`/work-orders/${ot.id}/detail-lines`);
      setOtDetailLines((prev) => ({ ...prev, [ot.id]: res.data || [] }));
    } catch {
      setOtDetailLines((prev) => ({ ...prev, [ot.id]: [] }));
    } finally {
      setOtDetailLoading((prev) => { const next = new Set(prev); next.delete(ot.id); return next; });
    }
  };

  // Modal de compres
  const [showCompresModal, setShowCompresModal] = useState(false);
  const [purchases, setPurchases] = useState<any[]>([]);
  const [loadingCompres, setLoadingCompres] = useState(false);

  // Modal d'hores
  const [showHoresModal, setShowHoresModal] = useState(false);
  const [workLogs, setWorkLogs] = useState<WorkLogEntry[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [horesForm, setHoresForm] = useState({
    workOrderId: '',
    workDate: toISO(new Date()),
    hours: 0,
    employeeId: '',
    description: '',
  });
  const [savingLog, setSavingLog] = useState(false);

  // Informe imprimible — mostra nomes les targetes "Previsions i Resultats"
  // i "Estadístiques" (amb els gràfics reals) forçades obertes, amagant la
  // resta de columnes/capçalera mentre dura la impressió.
  const [printingInforme, setPrintingInforme] = useState(false);
  const handlePrintInforme = () => { setTab('general'); setPrintingInforme(true); };
  useEffect(() => {
    if (!printingInforme) return;
    const t = setTimeout(() => {
      window.print();
      setPrintingInforme(false);
    }, 150);
    return () => clearTimeout(t);
  }, [printingInforme]);

  const fetchProject = useCallback(async () => {
    try {
      console.log('[v0] Fetching project with ID:', projectId);
      const response = await api.get<{ success: boolean; data: any }>(`/taller/projects/${projectId}`);
      console.log('[v0] Project response:', response);
      if (response.success && response.data) {
        // Normalitzar camps snake_case a camelCase
        const p = response.data;
        const normalizedProject: Project = {
          id: p.id,
          expedientNumber: p.expedientNumber || p.expedient_number,
          name: p.name,
          description: p.description,
          productCode: p.productCode || p.product_code,
          units: Number(p.units) || 1,
          unitType: p.unitType || p.unit_type || 'Ut',
          brandId: p.brandId || p.brand_id,
          clientId: p.clientId || p.client_id,
          clientName: p.clientName || p.client_name || p.client?.name,
          brandName: p.brandName || p.brand_name || p.brand?.name,
          startDate: p.startDate || p.start_date,
          expectedEndDate: p.expectedEndDate || p.expected_end_date,
          duration: p.duration,
          notes: p.notes,
          status: p.status,
          prevHours: Number(p.prevHours || p.prev_hours || 0),
          realHours: Number(p.realHours || p.real_hours || 0),
          estimatedRevenue: Number(p.estimatedRevenue || p.estimated_revenue || 0),
          actualRevenue: Number(p.actualRevenue || p.actual_revenue || 0),
          estimatedMaterialCost: Number(p.estimatedMaterialCost || p.estimated_material_cost || 0),
          estimatedLaborCost: Number(p.estimatedLaborCost || p.estimated_labor_cost || 0),
          actualLaborCost: Number(p.actualLaborCost || p.actual_labor_cost || 0),
          estimatedServiceCost: Number(p.estimatedOtherCost || p.estimated_other_cost || 0),
          actualServiceCost: Number(p.actualOtherCost || p.actual_other_cost || 0),
          actualMaterialCost: Number(p.actualMaterialCost || p.actual_material_cost || 0),
          budgetId: p.budgetId || p.budget_id || null,
          createdFromBudget: Number(p.createdFromBudget ?? p.created_from_budget ?? 0),
          cronogramaChapterOrder: Array.isArray(p.cronogramaChapterOrder || p.cronograma_chapter_order) ? (p.cronogramaChapterOrder || p.cronograma_chapter_order) : [],
          cronogramaLines: Array.isArray(p.cronogramaLines || p.cronograma_lines) ? (p.cronogramaLines || p.cronograma_lines) : [],
        };
        console.log('[v0] Normalized project:', normalizedProject);
        setProject(normalizedProject);
        // La fitxa es sempre editable (no hi ha mode "nomes lectura" separat, 2026-10-08) —
        // editForm s'omple directament en carregar, no nomes en clicar un boto "Editar".
        setEditForm({
          name: normalizedProject.name || '',
          brandId: normalizedProject.brandId || '',
          clientId: normalizedProject.clientId || '',
          productCode: normalizedProject.productCode || '',
          status: normalizedProject.status || 'ADJUDICAT',
          startDate: normalizedProject.startDate || '',
          expectedEndDate: normalizedProject.expectedEndDate || '',
          duration: normalizedProject.duration || '',
          units: normalizedProject.units || 1,
          unitType: normalizedProject.unitType || 'Ut',
          prevHours: normalizedProject.prevHours || 0,
          estimatedRevenue: normalizedProject.estimatedRevenue || 0,
          estimatedMaterialCost: normalizedProject.estimatedMaterialCost || 0,
          notes: normalizedProject.notes || '',
        });
      } else {
        console.log('[v0] No project data in response');
      }
    } catch (error) {
      console.error('[v0] Error fetching project:', error);
    }
  }, [projectId]);

  const fetchWorkOrders = useCallback(async () => {
    try {
      const response = await api.get<{ success: boolean; data: any[] }>(`/taller/projects/${projectId}/work-orders`);
      if (response.success && Array.isArray(response.data)) {
        const normalized: WorkOrder[] = response.data.map((wo: any) => ({
          id: wo.id,
          orderNumber: wo.orderNumber || wo.order_number || '',
          name: wo.name || '',
          description: wo.description,
          units: Number(wo.units || 1),
          unitType: wo.unitType || wo.unit_type || 'Ut',
          prevHours: Number(wo.prevHours || wo.prev_hours || 0),
          realHours: Number(wo.realHours || wo.real_hours || 0),
          prevMaterials: Number(wo.prevMaterials || wo.prev_materials || 0),
          realPurchases: Number(wo.realPurchases || wo.real_purchases || 0),
          realLabor: Number(wo.realLabor || wo.real_labor || 0),
          status: wo.status || 'pending',
          typeId: Number(wo.typeId || wo.type_id || 0),
          type: wo.type || undefined,
          nodeId: wo.nodeId || wo.node_id || undefined,
          projectBudgetLineId: wo.projectBudgetLineId || wo.project_budget_line_id || undefined,
          budgetLineIndex: wo.budgetLineIndex ?? wo.budget_line_index ?? undefined,
          groupedBudgetLines: parseGroupedBudgetLines(wo.groupedBudgetLines ?? wo.grouped_budget_lines),
          assignedToId: wo.assignedToId || wo.assigned_to_id || undefined,
          plannedStartDate: toISODateOrUndefined(wo.plannedStartDate ?? wo.planned_start_date),
          plannedEndDate: toISODateOrUndefined(wo.plannedEndDate ?? wo.planned_end_date),
        }));
        setWorkOrders(normalized);
      }
    } catch (error) {
      console.error('Error fetching work orders:', error);
      setWorkOrders([]);
    }
  }, [projectId]);

  const fetchWorkLogs = useCallback(async () => {
    try {
      const response = await api.get<{ success: boolean; data: WorkLogEntry[] }>(`/work-logs?projectId=${projectId}`);
      if (response.success && Array.isArray(response.data)) {
        setWorkLogs(response.data);
      }
    } catch (error) {
      console.error('Error fetching work logs:', error);
      setWorkLogs([]);
    }
  }, [projectId]);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await api.get<{ success: boolean; data: EmployeeOption[] }>('/employees');
      if (response.success && Array.isArray(response.data)) {
        setEmployees(response.data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    }
  }, []);

  const handleAddWorkLog = async () => {
    if (!horesForm.workOrderId || !horesForm.workDate || !horesForm.hours || !horesForm.employeeId) return;
    setSavingLog(true);
    try {
      await api.post('/work-logs', { ...horesForm, projectId });
      setHoresForm(f => ({ ...f, hours: 0, description: '' }));
      await Promise.all([fetchWorkLogs(), fetchWorkOrders(), fetchProject()]);
    } catch (error) {
      console.error('Error adding work log:', error);
      alert('Error en desar el registre d\'hores');
    } finally {
      setSavingLog(false);
    }
  };

  const handleDeleteWorkLog = async (logId: string) => {
    if (!confirm('Segur que vols eliminar aquest registre?')) return;
    try {
      await api.delete(`/work-logs/${logId}`);
      await Promise.all([fetchWorkLogs(), fetchWorkOrders(), fetchProject()]);
    } catch (error) {
      console.error('Error deleting work log:', error);
    }
  };

  const handleOpenCompres = async () => {
    setShowCompresModal(true);
    setLoadingCompres(true);
    try {
      const response = await api.get<{ success: boolean; data: any[] }>(
        `/purchase-orders?projectId=${projectId}`
      );
      if (response.success && Array.isArray(response.data)) {
        setPurchases(response.data);
      }
    } catch (error) {
      console.error('Error fetching purchases:', error);
      setPurchases([]);
    } finally {
      setLoadingCompres(false);
    }
  };

  const handleOpenHores = async () => {
    setShowHoresModal(true);
    await Promise.all([fetchWorkLogs(), fetchEmployees()]);
    if (workOrders.length > 0 && !horesForm.workOrderId) {
      setHoresForm(f => ({ ...f, workOrderId: workOrders[0].id }));
    }
  };

  const fetchOTTypes = useCallback(async () => {
    try {
      const response = await api.get<{ success: boolean; data: WorkOrderTypeOption[] }>('/work-orders/types');
      if (response.success && response.data) {
        setOtTypes(response.data);
      }
    } catch (error) {
      console.error('Error fetching OT types:', error);
      // Fallback a tipus per defecte
      setOtTypes([
        { id: 1, code: 'DES', name: 'Despeses', sortOrder: 1 },
        { id: 2, code: 'GEN', name: 'General Oficina', sortOrder: 2 },
        { id: 3, code: 'MOB', name: 'Ma d\'obra', sortOrder: 3 },
        { id: 4, code: 'MAT', name: 'Materials', sortOrder: 4 },
        { id: 5, code: 'MUN', name: 'Muntatge', sortOrder: 5 },
        { id: 6, code: 'TAL', name: 'Taller', sortOrder: 6 },
      ]);
    }
  }, []);

  const fetchEscandalls = useCallback(async () => {
    try {
      const response = await api.get<{ success: boolean; data: EscandallOption[] }>(`/taller/projects/${projectId}/escandalls`);
      if (response.success && Array.isArray(response.data)) {
        setEscandalls(response.data);
      }
    } catch (error) {
      console.error('Error fetching escandalls:', error);
      setEscandalls([]);
    }
  }, [projectId]);

  const fetchBudgetLines = useCallback(async () => {
    try {
      const response = await api.get<{ success: boolean; data: BudgetLineOption[] }>(`/taller/projects/${projectId}/budget-lines`);
      if (response.success && Array.isArray(response.data)) {
        setBudgetLines(response.data);
      }
    } catch (error) {
      console.error('Error fetching budget lines:', error);
      setBudgetLines([]);
    }
  }, [projectId]);

  useEffect(() => {
    api.get<{ success: boolean; data: { code: string; name: string }[] }>('/nodes/unit-types')
      .then((r) => setUnitTypes(r.data ?? []))
      .catch(() => setUnitTypes([]));
  }, []);

  useEffect(() => {
    // En mode "Nou Expedient" no hi ha res a carregar (ni project, ni OT's,
    // ni escandalls/linies de pressupost, que depenen d'un projectId real).
    if (isNew) {
      setLoading(false);
      return;
    }
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchProject(),
        fetchWorkOrders(),
        fetchOTTypes(),
        fetchEscandalls(),
        fetchBudgetLines(),
        fetchEmployees(),
      ]);
      setLoading(false);
    };
    loadData();
  }, [isNew, fetchProject, fetchWorkOrders, fetchOTTypes, fetchEscandalls, fetchBudgetLines, fetchEmployees]);

  // Marca/Client/Tipus de producte — nomes calen per al formulari d'edició
  // (Nou Expedient o "Editar"), es carreguen sempre igualment (llistes
  // petites) per no dependre de quan l'usuari entra en mode edició.
  useEffect(() => {
    api.get<{ success: boolean; data: Brand[] }>('/brands')
      .then((r) => setBrands(r.data || []))
      .catch(() => setBrands([]));
    api.get<{ success: boolean; data: ClientOption[] }>('/clients?limit=9999&sortBy=name')
      .then((r) => setClients(r.data || []))
      .catch(() => setClients([]));
    api.get<{ success: boolean; data: CompositeCodeOption[] }>('/catalog/composite-codes')
      .then((r) => setCompositeCodes(r.data || []))
      .catch(() => setCompositeCodes([]));
  }, []);

  const handleSaveEdit = async () => {
    if (!editForm.name) {
      alert("El nom es obligatori");
      return;
    }
    if (!editForm.brandId) {
      alert("La marca es obligatoria");
      return;
    }
    setSavingEdit(true);
    try {
      if (isNew) {
        await api.post<{ success: boolean; data: { id: string } }>('/taller/projects', editForm);
      } else {
        await api.put(`/taller/projects/${projectId}`, editForm);
      }
      navigate('/taller');
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Error en desar l\'expedient. Comprova les dades i torna-ho a intentar.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAddOT = () => {
    setEditingOT(null);
    setOtFormData({
      name: '',
      typeId: 0,
      nodeId: '',
      projectBudgetLineId: '',
      assignedToId: '',
      description: '',
      units: 1,
      unitType: 'Ut',
      prevHours: 0,
      prevMaterials: 0,
      plannedStartDate: '',
      plannedEndDate: '',
    });
    setShowOTModal(true);
  };

  const handleEditOT = (ot: WorkOrder) => {
    setEditingOT(ot);
    setOtFormData({
      name: ot.name,
      typeId: ot.typeId || 0,
      nodeId: ot.nodeId || '',
      projectBudgetLineId: ot.projectBudgetLineId || '',
      assignedToId: ot.assignedToId || '',
      description: ot.description || '',
      units: ot.units,
      unitType: ot.unitType,
      prevHours: ot.prevHours,
      prevMaterials: ot.prevMaterials,
      plannedStartDate: ot.plannedStartDate || '',
      plannedEndDate: ot.plannedEndDate || '',
    });
    setShowOTModal(true);
  };

  // En triar una línia del pressupost, es deriva l'escandall i es
  // pre-omplen Prev. Hores/Materials amb els valors d'aquella línia — el
  // backend fa el mateix càlcul per defecte, pero es reflecteix aquí a l'
  // instant perque l'usuari els vegi abans de desar.
  const handleSelectBudgetLine = (lineId: string) => {
    const line = budgetLines.find((l) => l.id === lineId);
    setOtFormData((prev) => ({
      ...prev,
      projectBudgetLineId: lineId,
      // En canviar la línia de pressupost es perd la línia de detall que
      // s'hagués triat (era d'una altra línia) — es reseteja perque no es
      // quedi apuntant a un índex d'un desglose diferent.
      budgetLineIndex: undefined,
      nodeId: line?.nodeId || '',
      name: prev.name || line?.concepte || '',
      prevHours: line ? Number(line.totalHores || 0) : prev.prevHours,
      prevMaterials: line ? Number(line.costMaterials || 0) : prev.prevMaterials,
    }));
  };

  // Mesura d'una línia del desglose (mateixa fórmula que BudgetForm.tsx
  // calcNodeDetailMeasurement): quantitat × dimensions informades.
  const escandallLineMeasurement = (l: EscandallDetailLine): number => {
    const qty = Number(l.quantity) || 0;
    const a = Number(l.dimensionA) || 0;
    const b = Number(l.dimensionB) || 0;
    const h = Number(l.dimensionH) || 0;
    if (a > 0 && b > 0 && h > 0) return qty * a * b * h;
    if (a > 0 && b > 0) return qty * a * b;
    if (a > 0) return qty * a;
    return qty;
  };

  // Mateixes fórmules que el Detall real de l'escandall (NodeForm.tsx) — la
  // quantitat/mides de cada fila del desglose ja són el valor real
  // d'execució (ex. 755 m² de solera), NO "per 1 unitat a repetir Qt
  // vegades"; per això NO s'escala per lineQtyOf(line) (el Qt de la
  // capçalera és només informatiu, no multiplica el desglose).
  const computeSublineCalc = (sub: EscandallDetailLine, _line: BudgetLineOption) => {
    const measurement = escandallLineMeasurement(sub);
    const costU = Number(sub.resolvedUnitCost || 0);
    const costTotal = measurement * costU;
    const marge = Number(sub.marge || 0);
    const descompte = Number(sub.descompte || 0);
    const preu = costU * marge * (1 - descompte / 100);
    const importVal = measurement * preu;
    const pes = Number(sub.pesTotal || 0);
    return { measurement, costU, costTotal, marge, descompte, preu, importVal, pes };
  };

  // Aplana una partida del desglose al mateix format DetallDisplayRow que fa servir
  // l'exportació a Excel dels Escandalls (NodeForm.tsx) — vegeu ../compras/detallExcelExport.
  const sublineToDisplayRow = (sub: EscandallDetailLine, index: number, line: BudgetLineOption): DetallDisplayRow => {
    const { measurement, costU, costTotal, marge, descompte, preu, importVal, pes } = computeSublineCalc(sub, line);
    return {
      key: `${line.id}-${index}`,
      num: String(index + 1),
      cap: sub.chapter || '',
      planol: sub.referencia || '',
      comentari: sub.detail || '',
      element: sub.description || '',
      badge: badgeFor((sub.childType as any) || 'MATERIAL', sub.nature || 'MATERIAL'),
      ua: sub.unit || '',
      un: sub.quantity ? fmt2(sub.quantity) : '',
      a: sub.dimensionA ? fmt3(sub.dimensionA) : '',
      b: sub.dimensionB ? fmt3(sub.dimensionB) : '',
      c: sub.dimensionH ? fmt3(sub.dimensionH) : '',
      amidament: fmt2(measurement),
      locked: !!sub.isPriceLocked,
      costU,
      costTotal,
      marge,
      descompte: descompte ? fmt2(descompte) : '',
      preu,
      import: importVal,
      observacions: sub.observations || '',
      pes,
      elementImageUrl: sub.elementImageUrl ?? null,
    };
  };

  // Totes les línies del pressupost (partides), cadascuna amb el seu propi resum
  // (Qt/Ut/Concepte/Total cost/Preu/Preu-U/Resultat/Marge%, com es veu a la capçalera de la
  // fila a "Línies del Expedient") i el seu desglose agrupat per capítol — abans nomes
  // s'exportava el desglose i es perdien les dades de la partida.
  const handleExportDetallExcel = async () => {
    const lines = budgetLines
      .map((line) => {
        const draft = effectiveBudgetLine(line);
        const sublines = draft.escandallLines ?? [];
        const totalCost = recalcTotalCostFromDesglose(line, sublines);
        const preu = Number(draft.preuUT || 0);
        const qt = Number(draft.medicio || 0);
        const resultat = preu - totalCost;
        const partida: PartidaSummary = {
          ordre: draft.ordre ?? '—',
          concepte: draft.concepte || '',
          qt,
          ut: draft.tipus || draft.unitat || '',
          totalCost,
          preu,
          preuU: preu / (qt || 1),
          resultat,
          margePct: preu > 0 ? (resultat / preu) * 100 : 0,
        };
        const groups = groupByChapter(sublines).map((g) => ({
          key: `${line.id}-${g.chapter}`,
          label: g.chapter === SENSE_CHAPTER ? 'Desglose' : g.chapter,
          rows: g.items.map(({ item, originalIdx }) => sublineToDisplayRow(item, originalIdx, line)),
        })).filter((g) => g.rows.length > 0);
        return { partida, groups };
      })
      .filter((l) => l.groups.length > 0);

    if (lines.length === 0) {
      alert('No hi ha cap línia per exportar.');
      return;
    }
    // Amb una sola línia de pressupost, el seu concepte (text descriptiu) es mes útil al
    // títol que el nom curt de l'expedient — amb vàries línies no hi ha un concepte únic
    // a mostrar, així que es manté nomes el nom de l'expedient.
    const base = project?.name || 'Línies del Expedient';
    const title = budgetLines.length === 1 && budgetLines[0].concepte
      ? `${base} — ${budgetLines[0].concepte}`
      : `${base} — Línies`;
    // El nom de fitxer ha de ser curt (el concepte pot tenir centenars de caràcters i Windows
    // no permet obrir rutes massa llargues) — el títol complet es manté dins del full, nomes
    // es trunca el que es fa servir per al nom de l'arxiu descarregat.
    const filenameBase = title.length > 120 ? `${title.slice(0, 120)}…` : title;
    await exportLiniesAmbPartidesToExcel(`${sanitizeFilename(filenameBase)}.xls`, lines, title);
  };

  // Cada nature del desglose es correspon amb un tipus d'OT del catàleg (Material/Fabricació/
  // Serveis) — dins de la mà d'obra (MO_PROPIA/MO_SUBCONTRACT), "oficina"/"muntatge" a la
  // descripció la desvien a Oficina tècnica/Muntatge (mateixa heurística que
  // costAnalysis.service.ts/workOrder.service.ts), la resta es Fabricació.
  const otTypeCodeForNature = (nature?: string, description?: string): string | null => {
    if (nature === 'MATERIAL') return 'MAT';
    if (nature === 'MO_PROPIA' || nature === 'MO_SUBCONTRACT') {
      const text = description || '';
      if (/oficina/i.test(text)) return 'OFI';
      if (/muntatge/i.test(text)) return 'MUN';
      return 'TRE';
    }
    if (nature === 'SERVEIS') return 'SRV';
    return null;
  };

  // Obre "Nova OT" amb una línia concreta del desglose de l'escandall
  // (material/mà d'obra/servei) ja preseleccionada — des de la taula
  // "Línies del pressupost". El tipus d'OT es proposa segons la nature de
  // la línia, i Prev. Hores/Materials es deriven de la seva mesura.
  const handleCreateOTForSubline = (line: BudgetLineOption, sub: EscandallDetailLine, index: number) => {
    const typeCode = otTypeCodeForNature(sub.nature, sub.description);
    const type = otTypes.find((t) => t.code === typeCode);
    const { hours, materials } = sublineContribution(sub);
    setEditingOT(null);
    setOtFormData({
      name: sub.description || line.concepte,
      typeId: type?.id || 0,
      nodeId: line.nodeId || '',
      projectBudgetLineId: line.id,
      budgetLineIndex: index,
      assignedToId: '',
      description: '',
      units: 1,
      unitType: sub.unit || 'Ut',
      prevHours: hours,
      prevMaterials: materials,
      plannedStartDate: '',
      plannedEndDate: '',
    });
    setShowOTModal(true);
  };

  // Recull les partides seleccionades (checkbox) com a referències completes
  // {line, sub, index} i valida que totes comparteixin naturalesa — pas
  // previ compartit tant per crear una OT nova com per afegir-les a una ja
  // existent. Es poden mesclar partides de diferent naturalesa (MT/MO/SV/AL)
  // en una mateixa OT — cadascuna aporta a Prev. Hores o Prev. Materials
  // segons la seva pròpia naturalesa.
  const collectSelectedSublineRefs = (): { line: BudgetLineOption; sub: EscandallDetailLine; index: number }[] | null => {
    const refs: { line: BudgetLineOption; sub: EscandallDetailLine; index: number }[] = [];
    for (const key of selectedSublines) {
      const [lineId, indexStr] = key.split('::');
      const line = budgetLines.find((l) => l.id === lineId);
      const index = Number(indexStr);
      const sub = line?.escandallLines?.[index];
      if (!line || !sub) continue;
      refs.push({ line, sub, index });
    }
    return refs.length > 0 ? refs : null;
  };

  // Hores/import que aporta una partida del desglose segons la seva pròpia
  // naturalesa — reutilitzat per sumar correctament encara que la selecció
  // barregi tipus (mà d'obra + materials + serveis...). "materials" és
  // l'import en € de la partida — per a mà d'obra, resolvedUnitCost ja és
  // €/hora, així que també dona un import real, no nomes hores.
  const sublineContribution = (sub: EscandallDetailLine): { hours: number; materials: number } => {
    const measurement = escandallLineMeasurement(sub);
    const isHours = sub.nature === 'MO_PROPIA' || sub.nature === 'MO_SUBCONTRACT';
    return {
      hours: isHours ? measurement : 0,
      materials: measurement * Number(sub.resolvedUnitCost || 0),
    };
  };

  // Creació agrupada (2026-10-06): UNA sola OT que consolida totes les partides
  // seleccionades a "Línies del Expedient" (poden venir de línies de pressupost
  // diferents i de naturaleses diferents — la gràcia es poder agrupar-ho tot
  // en una OT: mà d'obra, materials, serveis...). Cada partida es queda al
  // seu capítol/línia d'origen; grouped_budget_lines guarda la llista
  // completa de referències perquè es pugui saber quines partides ja estan
  // "ocupades" per aquesta OT.
  const handleBulkCreateOTs = async () => {
    const refs = collectSelectedSublineRefs();
    if (!refs) return;

    const nature = refs[0].sub.nature;
    const typeCode = otTypeCodeForNature(nature, refs[0].sub.description);
    const type = otTypes.find((t) => t.code === typeCode);

    let prevHours = 0;
    let prevMaterials = 0;
    for (const { sub } of refs) {
      const { hours, materials } = sublineContribution(sub);
      prevHours += hours;
      prevMaterials += materials;
    }

    const firstLine = refs[0].line;
    if (!confirm(`Crear 1 OT agrupant ${refs.length} partides seleccionades?`)) return;
    setCreatingBulkOTs(true);
    try {
      await api.post(`/taller/projects/${projectId}/work-orders`, {
        projectId,
        name: type?.name || firstLine.concepte,
        typeId: type?.id || 0,
        nodeId: firstLine.nodeId || '',
        projectBudgetLineId: firstLine.id,
        budgetLineIndex: refs[0].index,
        groupedBudgetLines: refs.map((r) => ({ projectBudgetLineId: r.line.id, budgetLineIndex: r.index })),
        units: 1,
        unitType: refs[0].sub.unit || 'Ut',
        prevHours,
        prevMaterials,
      });
      setSelectedSublines(new Set());
      await fetchWorkOrders();
    } catch (error) {
      console.error('Error creating grouped work order:', error);
      alert("Error en crear la OT agrupada. Comprova les dades i torna-ho a intentar.");
    } finally {
      setCreatingBulkOTs(false);
    }
  };

  // Afegeix les partides seleccionades a una OT JA creada (2026-10-06) — per
  // quan es vol ampliar una OT agrupada amb línies que no s'havien
  // seleccionat en crear-la. Es poden barrejar naturaleses diferents.
  const [appendingToOT, setAppendingToOT] = useState(false);
  const handleAppendToExistingOT = async (otId: string) => {
    const refs = collectSelectedSublineRefs();
    if (!refs) return;
    const ot = workOrders.find((w) => w.id === otId);
    if (!ot) return;

    let addHours = 0;
    let addMaterials = 0;
    for (const { sub } of refs) {
      const { hours, materials } = sublineContribution(sub);
      addHours += hours;
      addMaterials += materials;
    }

    const existingGrouped = Array.isArray(ot.groupedBudgetLines)
      ? ot.groupedBudgetLines
      : (ot.projectBudgetLineId && ot.budgetLineIndex != null
          ? [{ projectBudgetLineId: ot.projectBudgetLineId, budgetLineIndex: ot.budgetLineIndex }]
          : []);
    const merged = [...existingGrouped];
    for (const r of refs) {
      const already = merged.some((g) => g.projectBudgetLineId === r.line.id && g.budgetLineIndex === r.index);
      if (!already) merged.push({ projectBudgetLineId: r.line.id, budgetLineIndex: r.index });
    }

    if (!confirm(`Afegir ${refs.length} partides a l'OT ${ot.orderNumber}?`)) return;
    setAppendingToOT(true);
    try {
      await api.put(`/work-orders/${otId}`, {
        groupedBudgetLines: merged,
        prevHours: Number(ot.prevHours || 0) + addHours,
        prevMaterials: Number(ot.prevMaterials || 0) + addMaterials,
      });
      setSelectedSublines(new Set());
      await fetchWorkOrders();
    } catch (error) {
      console.error('Error afegint partides a la OT:', error);
      alert("Error en afegir les partides a la OT. Comprova les dades i torna-ho a intentar.");
    } finally {
      setAppendingToOT(false);
    }
  };

  // Reimporta les línies del pressupost des del pressupost actual (2026-10-05) — per si s'ha editat
  // després d'acceptar-se (el snapshot ProjectBudgetLine queda congelat en generar l'EP). Les
  // línies que ja tenen OT's s'actualitzen igualment (confirmat per l'usuari abans de cridar-ho);
  // el backend avisa quantes OT's podrien haver quedat desincronitzades amb la nova dada.
  const handleReimportBudgetLines = async () => {
    if (!confirm(
      "ATENCIÓ: això sobreescriurà totes les línies amb el contingut actual del pressupost. " +
      "Qualsevol edició manual feta aquí a l'Expedient de Taller (línies afegides, esborrades o " +
      "modificades) es PERDRÀ. Les línies que ja tinguin OT's creades també s'actualitzaran (poden " +
      "quedar desincronitzades amb l'import/hores ja imputats). Vols continuar?"
    )) return;
    setReimportingBudgetLines(true);
    try {
      const response = await api.post<{ success: boolean; summary: { updated: number; created: number; deleted: number; skippedDeletions: number; workOrdersAffected: number } }>(
        `/taller/projects/${projectId}/budget-lines/reimport`, {}
      );
      await fetchBudgetLines();
      setSelectedSublines(new Set());
      const s = response.summary;
      let msg = `Reimportat: ${s.updated} línies actualitzades, ${s.created} de noves, ${s.deleted} eliminades.`;
      if (s.skippedDeletions > 0) msg += ` ${s.skippedDeletions} línies eliminades del pressupost no s'han esborrat perquè tenien OT's creades.`;
      if (s.workOrdersAffected > 0) msg += ` ${s.workOrdersAffected} OT's existents apunten a línies que han canviat de contingut — revisa-les.`;
      alert(msg);
    } catch (error) {
      console.error('Error reimporting budget lines:', error);
      alert('Error en reimportar les línies del pressupost. Torna-ho a intentar.');
    } finally {
      setReimportingBudgetLines(false);
    }
  };

  // Mateixa derivació que handleCreateOTForSubline pero sense obrir/tancar
  // el modal — es fa servir des del desplegable "Línia de detall" dins
  // "Nova OT", per poder triar/canviar la línia sense tornar a la taula.
  const handleSelectSubline = (line: BudgetLineOption, indexStr: string) => {
    if (indexStr === '') {
      setOtFormData((prev) => ({ ...prev, budgetLineIndex: undefined }));
      return;
    }
    const index = Number(indexStr);
    const sub = (line.escandallLines ?? [])[index];
    if (!sub) return;
    const typeCode = otTypeCodeForNature(sub.nature, sub.description);
    const type = otTypes.find((t) => t.code === typeCode);
    const { hours, materials } = sublineContribution(sub);
    setOtFormData((prev) => ({
      ...prev,
      budgetLineIndex: index,
      name: sub.description || line.concepte,
      typeId: type?.id || prev.typeId,
      unitType: sub.unit || prev.unitType,
      prevHours: hours,
      prevMaterials: materials,
    }));
  };

  const handleSaveOT = async () => {
    if (!otFormData.typeId || otFormData.typeId === 0) {
      alert('Cal seleccionar un tipus d\'OT');
      return;
    }
    // El nom de la OT es sempre el del tipus seleccionat al catàleg — no hi
    // ha cap camp per personalitzar-lo al formulari. Es resincronitza aquí
    // (no nomes a l'onChange del <select>) perque en editar una OT
    // existent el tipus ja ve preseleccionat i el canvi mai es dispara si
    // l'usuari no toca el desplegable, deixant el nom antic del catàleg
    // (p.ex. "Treball") encara que el catàleg s'hagi renombrat despres.
    const selectedType = otTypes.find((t) => t.id === otFormData.typeId);
    // @IsOptional() del DTO nomes salta la validacio si el valor es undefined/null, no si
    // es string buit — cal convertir '' a undefined perque @IsDateString() no el rebutgi.
    const payload = {
      ...otFormData,
      name: selectedType?.name || otFormData.name,
      plannedStartDate: otFormData.plannedStartDate || undefined,
      plannedEndDate: otFormData.plannedEndDate || undefined,
      // @IsOptional() nomes salta la validacio si es undefined/null — a diferencia de
      // nodeId/projectBudgetLineId (que usen @ValidateIf i ja toleren ''), assignedToId
      // usa @IsUUID() directe i el rebutja si arriba com a string buit.
      assignedToId: otFormData.assignedToId || undefined,
    };
    try {
      if (editingOT) {
        await api.put(`/work-orders/${editingOT.id}`, payload);
      } else {
        await api.post(`/taller/projects/${projectId}/work-orders`, payload);
      }
      setShowOTModal(false);
      fetchWorkOrders();
    } catch (error) {
      console.error('Error saving work order:', error);
      alert('Error en desar l\'OT. Comprova les dades i torna-ho a intentar.');
    }
  };

  const handleDeleteOT = async (otId: string) => {
    if (!confirm('Segur que vols eliminar aquesta OT?')) return;
    try {
      await api.delete(`/work-orders/${otId}`);
      fetchWorkOrders();
    } catch (error) {
      console.error('Error deleting work order:', error);
    }
  };

  const handlePrintFullOT = () => {
    if (!project) return;
    const fmt = (n: number | undefined | null) =>
      (n ?? 0).toLocaleString('ca-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const fmtDate = (d: string | undefined | null) =>
      d ? new Date(d).toLocaleDateString('ca-ES') : '—';

    const tPrevH  = workOrders.reduce((s, o) => s + (o.prevHours || 0), 0);
    const tRealH  = workOrders.reduce((s, o) => s + (o.realHours || 0), 0);
    const tPrevM  = workOrders.reduce((s, o) => s + (o.prevMaterials || 0), 0);
    const tRealM  = workOrders.reduce((s, o) => s + (o.realPurchases || 0) + (o.realLabor || 0), 0);

    const otRows = workOrders.map(ot => `
      <tr>
        <td>${ot.orderNumber}</td>
        <td>${ot.name}</td>
        <td style="text-align:right">${fmt(ot.units)} ${ot.unitType}</td>
        <td style="text-align:right">${fmt(ot.prevHours)}</td>
        <td style="text-align:right">${fmt(ot.realHours)}</td>
        <td style="text-align:right;${(ot.realHours - ot.prevHours) > 0 ? 'color:#dc2626' : 'color:#16a34a'}">${fmt(ot.realHours - ot.prevHours)}</td>
        <td style="text-align:right">${fmt(ot.prevMaterials)}</td>
        <td style="text-align:right">${fmt((ot.realPurchases || 0) + (ot.realLabor || 0))}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html>
<html lang="ca">
<head>
  <meta charset="utf-8">
  <title>Full OT — Exp. ${project.expedientNumber}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 9pt; color: #1e293b; padding: 1.5cm 2cm; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1e293b; padding-bottom: 8px; margin-bottom: 12px; }
    .header h1 { font-size: 14pt; font-weight: bold; }
    .header .exp { font-size: 20pt; font-weight: bold; color: #1e40af; }
    .company { text-align: right; font-size: 8pt; color: #64748b; }
    .info-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 6px 12px; margin-bottom: 14px; padding: 8px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px; }
    .info-item label { display: block; font-size: 7pt; color: #64748b; text-transform: uppercase; letter-spacing: .04em; }
    .info-item span { font-weight: 600; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
    th { background: #1e293b; color: white; padding: 5px 6px; text-align: left; font-size: 8pt; }
    th.r, td.r { text-align: right; }
    td { padding: 4px 6px; border-bottom: 1px solid #e2e8f0; font-size: 8.5pt; }
    tr:nth-child(even) td { background: #f8fafc; }
    tfoot td { background: #e2e8f0 !important; font-weight: bold; border-top: 2px solid #94a3b8; }
    .section-title { font-size: 9pt; font-weight: bold; text-transform: uppercase; letter-spacing: .05em; color: #475569; margin-bottom: 4px; }
    @page { size: A4 landscape; margin: 0; }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="exp">${project.expedientNumber}</div>
      <h1>${project.name}</h1>
      <div style="font-size:8pt;color:#64748b;margin-top:2px">Full d'Ordres de Treball · Imprès el ${new Date().toLocaleDateString('ca-ES')}</div>
    </div>
    <div class="company">
      <strong>ROIG I FILLS ASSOCIATS S.L.</strong><br>
      Gestió Integral
    </div>
  </div>

  <div class="info-grid">
    <div class="info-item"><label>Client</label><span>${project.clientName || '—'}</span></div>
    <div class="info-item"><label>Marca</label><span>${project.brandName || '—'}</span></div>
    <div class="info-item"><label>Codi producte</label><span>${project.productCode || '—'}</span></div>
    <div class="info-item"><label>Unitats</label><span>${fmt(project.units)} ${project.unitType}</span></div>
    <div class="info-item"><label>Data inici</label><span>${fmtDate(project.startDate)}</span></div>
    <div class="info-item"><label>Data lliurament</label><span>${fmtDate(project.expectedEndDate)}</span></div>
    <div class="info-item"><label>Durada</label><span>${project.duration || '—'}</span></div>
    <div class="info-item"><label>Estat</label><span>${PROJECT_STATUS_LABELS[project.status]?.label ?? project.status}</span></div>
  </div>

  <div class="section-title">Ordres de Treball</div>
  <table>
    <thead>
      <tr>
        <th>Num OT</th>
        <th>Tipus / Nom</th>
        <th class="r">Unitats</th>
        <th class="r">Prev. Hores</th>
        <th class="r">Real Hores</th>
        <th class="r">Dif. Hores</th>
        <th class="r">Prev. Import €</th>
        <th class="r">Real Import €</th>
      </tr>
    </thead>
    <tbody>${otRows}</tbody>
    <tfoot>
      <tr>
        <td colspan="3" class="r">TOTALS</td>
        <td class="r">${fmt(tPrevH)}</td>
        <td class="r">${fmt(tRealH)}</td>
        <td class="r" style="${(tRealH - tPrevH) > 0 ? 'color:#dc2626' : 'color:#16a34a'}">${fmt(tRealH - tPrevH)}</td>
        <td class="r">${fmt(tPrevM)}</td>
        <td class="r">${fmt(tRealM)}</td>
      </tr>
    </tfoot>
  </table>

  <div class="section-title">Resum econòmic</div>
  <table style="width:50%">
    <thead>
      <tr>
        <th></th>
        <th class="r">Previsió</th>
        <th class="r">Real</th>
        <th class="r">Diferència</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Ingressos</td>
        <td class="r">${fmt(project.estimatedRevenue)} €</td>
        <td class="r">${fmt(project.actualRevenue)} €</td>
        <td class="r" style="${(project.actualRevenue - project.estimatedRevenue) < 0 ? 'color:#dc2626' : 'color:#16a34a'}">${fmt(project.actualRevenue - project.estimatedRevenue)} €</td>
      </tr>
      <tr>
        <td>Materials</td>
        <td class="r">${fmt(project.estimatedMaterialCost)} €</td>
        <td class="r">${fmt(project.actualMaterialCost)} €</td>
        <td class="r" style="${(project.actualMaterialCost - project.estimatedMaterialCost) > 0 ? 'color:#dc2626' : 'color:#16a34a'}">${fmt(project.actualMaterialCost - project.estimatedMaterialCost)} €</td>
      </tr>
      <tr>
        <td>Treball (30€/h)</td>
        <td class="r">${fmt(tPrevH * 30)} €</td>
        <td class="r">${fmt(tRealH * 30)} €</td>
        <td class="r" style="${(tRealH - tPrevH) > 0 ? 'color:#dc2626' : 'color:#16a34a'}">${fmt((tRealH - tPrevH) * 30)} €</td>
      </tr>
    </tbody>
    <tfoot>
      <tr>
        <td>Marge brut</td>
        <td class="r">${fmt(project.estimatedRevenue - project.estimatedMaterialCost - tPrevH * 30)} €</td>
        <td class="r">${fmt(project.actualRevenue - project.actualMaterialCost - tRealH * 30)} €</td>
        <td class="r"></td>
      </tr>
    </tfoot>
  </table>

  ${project.notes ? `<div style="margin-top:12px;padding:8px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:4px;font-size:8pt"><strong>Observacions:</strong> ${project.notes}</div>` : ''}
</body>
</html>`;

    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 600);
  };

  // Càlculs de totals
  const totalPrevHours = workOrders.reduce((sum, ot) => sum + (ot.prevHours || 0), 0);
  const totalRealHours = workOrders.reduce((sum, ot) => sum + (ot.realHours || 0), 0);
  const totalPrevMaterials = workOrders.reduce((sum, ot) => sum + (ot.prevMaterials || 0), 0);
  const totalRealMaterials = workOrders.reduce((sum, ot) => sum + (ot.realPurchases || 0) + (ot.realLabor || 0), 0);
  const totalInvoices = project?.actualRevenue ?? 0;
  const totalMaterialCost = project?.actualMaterialCost ?? 0;
  const totalServiceCost = project?.actualServiceCost ?? 0;
  // Cost real de mà d'obra — ve de project.actualLaborCost, que el backend
  // recalcula (recalculateProjectTotals) a partir de les hores imputades
  // per empleat a /work-logs (cost real per empleat, no una tarifa plana).
  const totalLaborCost = project?.actualLaborCost ?? 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!project && !isNew) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-500">No s&apos;ha trobat l&apos;expedient</p>
        <Button onClick={onBack} variant="outline" className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Tornar
        </Button>
      </div>
    );
  }

  const inputCls = 'w-full px-2.5 py-1.5 border border-gray-200 rounded text-[12px] focus:outline-none focus:ring-1 focus:ring-blue-400';
  const labelCls = 'block text-[12px] text-muted-foreground mb-0.5';

  // Formulari d'edició de les dades generals — es fa servir tant per crear
  // ("Nou Expedient", sense OT's ni previsions perque encara no existeixen)
  // com per editar-les dins la fitxa d'un expedient existent (on la taula
  // d'OT's i el panell de Previsions es queden visibles al costat, no
  // s'amaguen — nomes canvia aquesta targeta).
  const editFormCard = (
    <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
      <div>
        <label className={labelCls}>Nom / Descripció *</label>
        <input
          className={inputCls}
          value={editForm.name}
          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
          placeholder="Descripció de l'expedient"
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>Marca *</label>
          <select value={editForm.brandId} onChange={(e) => setEditForm({ ...editForm, brandId: e.target.value })} className={inputCls}>
            <option value="">Selecciona marca...</option>
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Client</label>
          <select value={editForm.clientId} onChange={(e) => setEditForm({ ...editForm, clientId: e.target.value })} className={inputCls}>
            <option value="">Selecciona client...</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Tipus de Producte</label>
          <select value={editForm.productCode} onChange={(e) => setEditForm({ ...editForm, productCode: e.target.value })} className={inputCls}>
            <option value="">Selecciona tipus...</option>
            {compositeCodes.map((cc) => (
              <option key={cc.id} value={cc.code}>{cc.code} - {cc.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Data Inici</label>
          <input type="date" className={inputCls} value={editForm.startDate} onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Data Lliurament</label>
          <input type="date" className={inputCls} value={editForm.expectedEndDate} onChange={(e) => setEditForm({ ...editForm, expectedEndDate: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Durada</label>
          <input className={inputCls} value={editForm.duration} onChange={(e) => setEditForm({ ...editForm, duration: e.target.value })} placeholder="Ex: 5 setmanes, 2 M..." />
        </div>
        <div>
          <label className={labelCls}>Unitats</label>
          <div className="flex gap-2">
            <input type="number" step="0.01" className={inputCls} value={editForm.units} onChange={(e) => setEditForm({ ...editForm, units: parseFloat(e.target.value) || 1 })} />
            <select value={editForm.unitType} onChange={(e) => setEditForm({ ...editForm, unitType: e.target.value })} className="w-20 px-2 py-1.5 border border-gray-200 rounded text-[12px] focus:outline-none focus:ring-1 focus:ring-blue-400">
              <option value="Ut">Ut</option>
              <option value="m2">m2</option>
              <option value="ml">ml</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 pt-3 border-t border-gray-100">
        <div>
          <label className={labelCls}>Prev. Hores</label>
          <input type="number" step="0.01" className={inputCls} value={editForm.prevHours} onChange={(e) => setEditForm({ ...editForm, prevHours: parseFloat(e.target.value) || 0 })} />
        </div>
        <div>
          <label className={labelCls}>Prev. Ingressos</label>
          <input type="number" step="0.01" className={inputCls} value={editForm.estimatedRevenue} onChange={(e) => setEditForm({ ...editForm, estimatedRevenue: parseFloat(e.target.value) || 0 })} />
        </div>
        <div>
          <label className={labelCls}>Prev. Compres</label>
          <input type="number" step="0.01" className={inputCls} value={editForm.estimatedMaterialCost} onChange={(e) => setEditForm({ ...editForm, estimatedMaterialCost: parseFloat(e.target.value) || 0 })} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header — mateix patró que ExpedientComercialForm.tsx/BudgetForm.tsx */}
      <DocumentHeader
        title={isNew ? 'Nou Expedient' : project!.expedientNumber}
        infoBadges={!isNew && (
          <>
            {editForm.brandId && <HeaderBadge tone="brand">{brands.find((b) => b.id === editForm.brandId)?.name ?? '—'}</HeaderBadge>}
            <span className="text-sm text-muted-foreground">{project!.name}</span>
          </>
        )}
        status={!isNew && (
          <StatusSelect
            value={editForm.status}
            options={Object.entries(PROJECT_STATUS_LABELS).map(([code, { label, color }]) => ({ code, name: label, className: color }))}
            fallbackLabel={editForm.status}
            onChange={(code) => setEditForm({ ...editForm, status: code })}
          />
        )}
        actions={<div className={printingInforme ? 'hidden' : 'flex items-center gap-2'}>
          {!isNew && (
            <>
              <button onClick={handlePrintFullOT} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded text-xs text-muted-foreground hover:bg-gray-50">
                <FileText size={14} /> Full OT
              </button>
              <button onClick={handlePrintInforme} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded text-xs text-muted-foreground hover:bg-gray-50">
                <FileSpreadsheet size={14} /> Informe
              </button>
              <button onClick={handleOpenHores} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded text-xs text-muted-foreground hover:bg-gray-50">
                <Clock size={14} /> Hores
              </button>
              <button onClick={handleOpenCompres} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded text-xs text-muted-foreground hover:bg-gray-50">
                <ShoppingCart size={14} /> Compres
              </button>
            </>
          )}
          <button onClick={onBack} className="px-4 py-2 border border-gray-200 rounded-lg text-xs text-gray-500 hover:bg-gray-50">
            Cancel·lar
          </button>
          <button onClick={handleSaveEdit} disabled={savingEdit} className="px-4 py-2 bg-[#2563EB] text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50">
            {savingEdit ? 'Desant...' : 'Desar'}
          </button>
        </div>}
      />

      {/* Pestanyes — nomes amb expedient ja creat (en "Nou Expedient" encara no hi ha
          línies de pressupost ni res a mostrar al Detall). */}
      {!isNew && !printingInforme && (
        <div className="bg-white border border-gray-200 rounded-lg px-6 py-3">
          <div className="grid grid-cols-3 gap-3 max-w-2xl">
            <button
              type="button"
              onClick={() => setTab('general')}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${tab === 'general' ? 'border-blue-500 bg-blue-50/50' : 'border-border bg-white hover:shadow-sm'}`}
            >
              <div className="p-1.5 rounded-lg bg-blue-500/10 shrink-0"><FileText className="h-4 w-4 text-blue-500" /></div>
              <span className="text-xs font-semibold">Dades Generals</span>
            </button>
            <button
              type="button"
              onClick={() => setTab('detall')}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${tab === 'detall' ? 'border-teal-500 bg-teal-50/50' : 'border-border bg-white hover:shadow-sm'}`}
            >
              <div className="p-1.5 rounded-lg bg-teal-500/10 shrink-0"><Table2 className="h-4 w-4 text-teal-500" /></div>
              <span className="text-xs font-semibold">Línies del Expedient</span>
            </button>
            <button
              type="button"
              onClick={() => setTab('cronograma')}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${tab === 'cronograma' ? 'border-amber-500 bg-amber-50/50' : 'border-border bg-white hover:shadow-sm'}`}
            >
              <div className="p-1.5 rounded-lg bg-amber-500/10 shrink-0"><CalendarRange className="h-4 w-4 text-amber-500" /></div>
              <span className="text-xs font-semibold">Cronograma</span>
            </button>
          </div>
        </div>
      )}

      {isNew ? editFormCard : project && (tab === 'general' || printingInforme) && (
      <div className={printingInforme ? 'grid grid-cols-1 gap-6' : 'grid grid-cols-2 gap-6'}>
        {/* Columna esquerra - dades principals (Informació general + OTs) */}
        <div className={printingInforme ? 'hidden' : 'space-y-6'}>
          {/* Informació general — sempre editable (2026-10-08, sense mode "nomes lectura"). */}
          {editFormCard}

          {/* Llista d'OTs */}
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">Ordres de Treball (OT&apos;s)</h3>
              <Button size="sm" onClick={handleAddOT}>
                <Plus className="w-4 h-4 mr-1" /> Nova OT
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="ordres-treball-table w-full text-[10px] text-muted-foreground">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-2 py-2 w-6"></th>
                    <th className="px-3 py-2 text-left font-medium text-muted-foreground">Num OT</th>
                    <th className="px-3 py-2 text-left font-medium text-muted-foreground">Tipus</th>
                    <th className="px-3 py-2 text-left font-medium text-muted-foreground">Descripció</th>
                    <th className="px-3 py-2 text-right font-medium text-muted-foreground">Prev. Hores</th>
                    <th className="px-3 py-2 text-right font-medium text-muted-foreground">Real Hores</th>
                    <th className="px-3 py-2 text-right font-medium text-muted-foreground">Prev. Import €</th>
                    <th className="px-3 py-2 text-right font-medium text-muted-foreground">Real Import €</th>
                    <th className="px-3 py-2 text-center font-medium text-muted-foreground">Accions</th>
                  </tr>
                </thead>
                <tbody>
                  {workOrders.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">
                        No hi ha OT&apos;s creades. Clica &quot;Nova OT&quot; per afegir-ne una.
                      </td>
                    </tr>
                  ) : (
                    workOrders.map((ot) => {
                      const isOtExpanded = expandedOTIds.has(ot.id);
                      const detailLines = otDetailLines[ot.id] ?? [];
                      return (
                      <Fragment key={ot.id}>
                      <tr className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-2 py-2 text-center">
                          <button onClick={() => toggleOTDetail(ot)} className="p-0.5 rounded hover:bg-gray-100 text-gray-400">
                            {isOtExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </button>
                        </td>
                        <td className="px-3 py-2 font-mono">{ot.orderNumber}</td>
                        <td className="px-3 py-2">{ot.name}</td>
                        <td className="px-3 py-2 text-muted-foreground">{ot.description || '—'}</td>
                        <td className="px-3 py-2 text-right">{formatNumber(ot.prevHours)}</td>
                        <td className="px-3 py-2 text-right">{formatNumber(ot.realHours)}</td>
                        <td className="px-3 py-2 text-right">{formatNumber(ot.prevMaterials)}</td>
                        <td className="px-3 py-2 text-right">{formatNumber((ot.realPurchases || 0) + (ot.realLabor || 0))}</td>
                        <td className="px-3 py-2 text-center">
                          <button onClick={() => handleEditOT(ot)} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-blue-600 transition-colors mr-1">
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => handleDeleteOT(ot.id)} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-red-600 transition-colors">
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                      {isOtExpanded && (
                        <tr className="border-t border-gray-100 bg-gray-50/50">
                          <td colSpan={9} className="px-4 py-3">
                            {otDetailLoading.has(ot.id) ? (
                              <p className="text-xs text-muted-foreground">Carregant detall...</p>
                            ) : detailLines.length === 0 ? (
                              <p className="text-xs text-muted-foreground">Aquesta OT no té cap línia de detall associada.</p>
                            ) : (
                              <div className="overflow-x-auto">
                                <table className="expedient-budget-lines-table w-full text-muted-foreground">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th className="px-2 py-1.5 text-left font-medium text-muted-foreground">Tipus</th>
                                      <th className="px-2 py-1.5 text-left font-medium text-muted-foreground">Línia pressupost</th>
                                      <th className="px-2 py-1.5 text-left font-medium text-muted-foreground">Ref. Plànol</th>
                                      <th className="px-2 py-1.5 text-left font-medium text-muted-foreground">Descripció</th>
                                      <th className="px-2 py-1.5 text-left font-medium text-muted-foreground">Element</th>
                                      <th className="px-2 py-1.5 text-center font-medium text-muted-foreground">UA</th>
                                      <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">UN</th>
                                      <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Cost/U</th>
                                      <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Cost total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {detailLines.map((sub, i) => {
                                      const lineQty = sub.__lineQty ?? 1;
                                      const measurement = escandallLineMeasurement(sub) * lineQty;
                                      const costU = Number(sub.resolvedUnitCost || 0);
                                      const costTotal = measurement * costU;
                                      const badge = badgeFor((sub.childType as any) || 'MATERIAL', sub.nature || 'MATERIAL');
                                      return (
                                        <tr key={i} className="border-t border-gray-100">
                                          <td className="px-2 py-1.5">
                                            <span className={`inline-block font-bold px-1 rounded ${badge.cls}`}>{badge.text}</span>
                                          </td>
                                          <td className="px-2 py-1.5 text-muted-foreground">{sub.__budgetLineConcepte || '—'}</td>
                                          <td className="px-2 py-1.5">{sub.referencia || '—'}</td>
                                          <td className="px-2 py-1.5">{sub.detail || '—'}</td>
                                          <td className="px-2 py-1.5">{sub.description || '—'}</td>
                                          <td className="px-2 py-1.5 text-center text-muted-foreground">{sub.unit || ''}</td>
                                          <td className="px-2 py-1.5 text-right">{formatNumber(measurement)}</td>
                                          <td className="px-2 py-1.5 text-right">{formatNumber(costU)}</td>
                                          <td className="px-2 py-1.5 text-right font-medium">{formatNumber(costTotal)}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                      </Fragment>
                      );
                    })
                  )}
                </tbody>
                {workOrders.length > 0 && (
                  <tfoot className="bg-gray-100 font-semibold">
                    <tr>
                      <td colSpan={3} className="px-3 py-2 text-right">TOTALS:</td>
                      <td className="px-3 py-2 text-right">{formatNumber(totalPrevHours)}</td>
                      <td className="px-3 py-2 text-right">{formatNumber(totalRealHours)}</td>
                      <td className="px-3 py-2 text-right">{formatNumber(totalPrevMaterials)}</td>
                      <td className="px-3 py-2 text-right">{formatNumber(totalRealMaterials)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

        </div>

        {/* Columna dreta - targetes colapsables (mateix patró que Expedients Comercials) */}
        <div className="space-y-6">
          {/* Previsions i Resultats */}
          <CollapsibleCard title="Previsions i Resultats" icon={FileSpreadsheet} iconClassName="text-blue-600" forceOpen={printingInforme || undefined}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-muted-foreground">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left"></th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">Previsio</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">%</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">Real</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">%</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">Diferencia</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">%</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="px-3 py-2 font-medium">Unitats</td>
                    <td className="px-3 py-2 text-right">{formatNumber(project.units)}</td>
                    <td className="px-3 py-2 text-right"></td>
                    <td className="px-3 py-2 text-right">{formatNumber(project.units)}</td>
                    <td className="px-3 py-2 text-right"></td>
                    <td className="px-3 py-2 text-right">0,00</td>
                    <td className="px-3 py-2 text-right">1,00</td>
                  </tr>
                  <tr className="border-t">
                    <td className="px-3 py-2 font-medium">Ingressos</td>
                    <td className="px-3 py-2 text-right">{formatNumber(project.estimatedRevenue)}</td>
                    <td className="px-3 py-2 text-right"></td>
                    <td className="px-3 py-2 text-right">{formatNumber(totalInvoices)}</td>
                    <td className="px-3 py-2 text-right"></td>
                    <td className={`px-3 py-2 text-right ${totalInvoices - (project.estimatedRevenue ?? 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatNumber(totalInvoices - (project.estimatedRevenue ?? 0))}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {project.estimatedRevenue ? formatNumber(totalInvoices / project.estimatedRevenue) : '-'}
                    </td>
                  </tr>
                  <tr className="border-t">
                    <td className="px-3 py-2 font-medium">Compres</td>
                    <td className="px-3 py-2 text-right">{formatNumber(project.estimatedMaterialCost)}</td>
                    <td className="px-3 py-2 text-right">
                      {project.estimatedRevenue ? formatNumber((project.estimatedMaterialCost ?? 0) / project.estimatedRevenue * 100) : '-'}
                    </td>
                    <td className="px-3 py-2 text-right">{formatNumber(totalMaterialCost)}</td>
                    <td className="px-3 py-2 text-right">
                      {totalInvoices ? formatNumber(totalMaterialCost / totalInvoices * 100) : '-'}
                    </td>
                    <td className={`px-3 py-2 text-right ${totalMaterialCost - (project.estimatedMaterialCost ?? 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatNumber(totalMaterialCost - (project.estimatedMaterialCost ?? 0))}
                    </td>
                    <td className="px-3 py-2 text-right"></td>
                  </tr>
                  <tr className="border-t">
                    <td className="px-3 py-2 font-medium">Serveis</td>
                    <td className="px-3 py-2 text-right">{formatNumber(project.estimatedServiceCost ?? 0)}</td>
                    <td className="px-3 py-2 text-right">
                      {project.estimatedRevenue ? formatNumber((project.estimatedServiceCost ?? 0) / project.estimatedRevenue * 100) : '-'}
                    </td>
                    <td className="px-3 py-2 text-right">{formatNumber(totalServiceCost)}</td>
                    <td className="px-3 py-2 text-right">
                      {totalInvoices ? formatNumber(totalServiceCost / totalInvoices * 100) : '-'}
                    </td>
                    <td className={`px-3 py-2 text-right ${totalServiceCost - (project.estimatedServiceCost ?? 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatNumber(totalServiceCost - (project.estimatedServiceCost ?? 0))}
                    </td>
                    <td className="px-3 py-2 text-right"></td>
                  </tr>
                  <tr className="border-t">
                    <td className="px-3 py-2 font-medium">Treball</td>
                    <td className="px-3 py-2 text-right">{formatNumber(project.estimatedLaborCost ?? 0)}</td>
                    <td className="px-3 py-2 text-right"></td>
                    <td className="px-3 py-2 text-right">{formatNumber(totalLaborCost)}</td>
                    <td className="px-3 py-2 text-right"></td>
                    <td className={`px-3 py-2 text-right ${totalLaborCost - (project.estimatedLaborCost ?? 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatNumber(totalLaborCost - (project.estimatedLaborCost ?? 0))}
                    </td>
                    <td className="px-3 py-2 text-right"></td>
                  </tr>
                  <tr className="border-t bg-gray-100 font-semibold">
                    <td className="px-3 py-2">Totals Costos</td>
                    <td className="px-3 py-2 text-right">{formatNumber((project.estimatedMaterialCost ?? 0) + (project.estimatedServiceCost ?? 0) + (project.estimatedLaborCost ?? 0))}</td>
                    <td className="px-3 py-2 text-right"></td>
                    <td className="px-3 py-2 text-right">{formatNumber(totalMaterialCost + totalServiceCost + totalLaborCost)}</td>
                    <td className="px-3 py-2 text-right"></td>
                    <td className="px-3 py-2 text-right">{formatNumber((totalMaterialCost + totalServiceCost + totalLaborCost) - ((project.estimatedMaterialCost ?? 0) + (project.estimatedServiceCost ?? 0) + (project.estimatedLaborCost ?? 0)))}</td>
                    <td className="px-3 py-2 text-right"></td>
                  </tr>
                  <tr className="border-t bg-blue-50 font-semibold">
                    <td className="px-3 py-2">Marge brut</td>
                    <td className="px-3 py-2 text-right">{formatNumber((project.estimatedRevenue ?? 0) - (project.estimatedMaterialCost ?? 0) - (project.estimatedServiceCost ?? 0) - (project.estimatedLaborCost ?? 0))}</td>
                    <td className="px-3 py-2 text-right">1,00</td>
                    <td className="px-3 py-2 text-right">{formatNumber(totalInvoices - totalMaterialCost - totalServiceCost - totalLaborCost)}</td>
                    <td className="px-3 py-2 text-right">1,00</td>
                    <td className={`px-3 py-2 text-right ${(totalInvoices - totalMaterialCost - totalServiceCost - totalLaborCost) - ((project.estimatedRevenue ?? 0) - (project.estimatedMaterialCost ?? 0) - (project.estimatedServiceCost ?? 0) - (project.estimatedLaborCost ?? 0)) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatNumber((totalInvoices - totalMaterialCost - totalServiceCost - totalLaborCost) - ((project.estimatedRevenue ?? 0) - (project.estimatedMaterialCost ?? 0) - (project.estimatedServiceCost ?? 0) - (project.estimatedLaborCost ?? 0)))}
                    </td>
                    <td className="px-3 py-2 text-right"></td>
                  </tr>
                  {/* Factor Marge */}
                  <tr className="border-t">
                    <td className="px-3 py-2 font-medium">Factor Marge</td>
                    <td className="px-3 py-2 text-right">
                      {(project.estimatedMaterialCost ?? 0) + (project.estimatedServiceCost ?? 0) + (project.estimatedLaborCost ?? 0) > 0
                        ? formatNumber(((project.estimatedRevenue ?? 0) - (project.estimatedMaterialCost ?? 0) - (project.estimatedServiceCost ?? 0) - (project.estimatedLaborCost ?? 0)) / ((project.estimatedMaterialCost ?? 0) + (project.estimatedServiceCost ?? 0) + (project.estimatedLaborCost ?? 0)))
                        : '0,00'}
                    </td>
                    <td className="px-3 py-2 text-right"></td>
                    <td className="px-3 py-2 text-right">
                      {totalMaterialCost + totalServiceCost + totalLaborCost > 0
                        ? formatNumber((totalInvoices - totalMaterialCost - totalServiceCost - totalLaborCost) / (totalMaterialCost + totalServiceCost + totalLaborCost))
                        : '0,00'}
                    </td>
                    <td className="px-3 py-2 text-right"></td>
                    <td className="px-3 py-2 text-right"></td>
                    <td className="px-3 py-2 text-right"></td>
                  </tr>
                  {/* Num. Hores */}
                  <tr className="border-t">
                    <td className="px-3 py-2 font-medium">Num. hores</td>
                    <td className="px-3 py-2 text-right">{formatNumber(project.prevHours ?? 0)}</td>
                    <td className="px-3 py-2 text-right"></td>
                    <td className="px-3 py-2 text-right">{formatNumber(totalRealHours)}</td>
                    <td className="px-3 py-2 text-right"></td>
                    <td className={`px-3 py-2 text-right ${totalRealHours - (project.prevHours ?? 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatNumber(totalRealHours - (project.prevHours ?? 0))}
                    </td>
                    <td className="px-3 py-2 text-right"></td>
                  </tr>
                  {/* Preu/Hora */}
                  <tr className="border-t">
                    <td className="px-3 py-2 font-medium">Preu/Hora</td>
                    <td className="px-3 py-2 text-right">
                      {(project.prevHours ?? 0) > 0
                        ? formatNumber(((project.estimatedRevenue ?? 0) - (project.estimatedMaterialCost ?? 0) - (project.estimatedServiceCost ?? 0)) / (project.prevHours ?? 0))
                        : '-'}
                    </td>
                    <td className="px-3 py-2 text-right"></td>
                    <td className="px-3 py-2 text-right">
                      {totalRealHours > 0
                        ? formatNumber((totalInvoices - totalMaterialCost - totalServiceCost) / totalRealHours)
                        : '-'}
                    </td>
                    <td className="px-3 py-2 text-right"></td>
                    <td className="px-3 py-2 text-right"></td>
                    <td className="px-3 py-2 text-right"></td>
                  </tr>
                  {/* Separador */}
                  <tr className="border-t-2 border-gray-300"></tr>
                  {/* Ingres/u */}
                  <tr className="border-t">
                    <td className="px-3 py-2 font-medium">Ingres/u</td>
                    <td className="px-3 py-2 text-right">
                      {(project.units ?? 0) > 0 ? formatNumber((project.estimatedRevenue ?? 0) / project.units!) : '0,00'}
                    </td>
                    <td className="px-3 py-2 text-right"></td>
                    <td className="px-3 py-2 text-right">
                      {(project.units ?? 0) > 0 ? formatNumber(totalInvoices / project.units!) : '0,00'}
                    </td>
                    <td className="px-3 py-2 text-right"></td>
                    <td className="px-3 py-2 text-right">
                      {(project.units ?? 0) > 0 ? formatNumber((totalInvoices - (project.estimatedRevenue ?? 0)) / project.units!) : '0,00'}
                    </td>
                    <td className="px-3 py-2 text-right"></td>
                  </tr>
                  {/* Desp./u */}
                  <tr className="border-t">
                    <td className="px-3 py-2 font-medium">Desp./u</td>
                    <td className="px-3 py-2 text-right">
                      {(project.units ?? 0) > 0 ? formatNumber(((project.estimatedMaterialCost ?? 0) + (project.estimatedServiceCost ?? 0) + (project.estimatedLaborCost ?? 0)) / project.units!) : '0,00'}
                    </td>
                    <td className="px-3 py-2 text-right"></td>
                    <td className="px-3 py-2 text-right">
                      {(project.units ?? 0) > 0 ? formatNumber((totalMaterialCost + totalServiceCost + totalLaborCost) / project.units!) : '0,00'}
                    </td>
                    <td className="px-3 py-2 text-right"></td>
                    <td className="px-3 py-2 text-right">
                      {(project.units ?? 0) > 0 ? formatNumber(((totalMaterialCost + totalServiceCost + totalLaborCost) - ((project.estimatedMaterialCost ?? 0) + (project.estimatedServiceCost ?? 0) + (project.estimatedLaborCost ?? 0))) / project.units!) : '0,00'}
                    </td>
                    <td className="px-3 py-2 text-right"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CollapsibleCard>

          {/* Estadístiques */}
          <CollapsibleCard title="Estadístiques" icon={BarChart3} iconClassName="text-purple-600" forceOpen={printingInforme || undefined}>
            <StatsPanel
              project={project}
              workOrders={workOrders}
              totalPrevHours={totalPrevHours}
              totalRealHours={totalRealHours}
              totalInvoices={totalInvoices}
              totalMaterialCost={totalMaterialCost}
              totalServiceCost={totalServiceCost}
              totalLaborCost={totalLaborCost}
            />
          </CollapsibleCard>
        </div>
      </div>
      )}

      {/* Línies del pressupost — pestanya a part (com Dades Generals/Detall a
          BudgetForm.tsx) perque hi càpiguen totes les columnes del desglose.
          A diferencia del pressupost (teòric), aquí es la realitat
          d'execució: totalment editable (afegir/eliminar línies i el seu
          desglose, com un Escandall) — vegeu protecció d'OT's a
          project.service.ts. Cada línia individual del desglose es pot
          convertir en una OT. */}
      {!isNew && tab === 'detall' && (
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">Línies del Expedient</h3>
            <div className="flex items-center gap-2">
              {selectedSublines.size > 0 && (() => {
                // Les OT's poden barrejar partides de qualsevol naturalesa, així
                // que el desplegable "Afegir a OT existent" llista totes les OT's
                // encara no finalitzades (completades/cancel·lades no s'editen).
                const candidateOTs = workOrders.filter((ot) => ot.status !== 'completed' && ot.status !== 'cancelled');
                return (
                  <>
                    <Button size="sm" className="h-7 px-2 text-[11px]" onClick={handleBulkCreateOTs} disabled={creatingBulkOTs}>
                      <Plus className="w-3 h-3 mr-1" />
                      {creatingBulkOTs ? 'Creant...' : `${selectedSublines.size} sel. - Crear OT`}
                    </Button>
                    {candidateOTs.length > 0 && (
                      <select
                        className="h-7 px-2 text-[11px] border border-gray-200 rounded bg-white"
                        value=""
                        disabled={appendingToOT}
                        onChange={(e) => { if (e.target.value) handleAppendToExistingOT(e.target.value); }}
                        title="Afegir les partides seleccionades a una OT ja creada"
                      >
                        <option value="">{appendingToOT ? 'Afegint...' : 'Afegir a OT existent...'}</option>
                        {candidateOTs.map((ot) => (
                          <option key={ot.id} value={ot.id}>{ot.orderNumber} — {ot.name}</option>
                        ))}
                      </select>
                    )}
                  </>
                );
              })()}
              <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={handleAddBudgetLine} disabled={addingBudgetLine}>
                <Plus className="w-3 h-3 mr-1" />
                {addingBudgetLine ? 'Afegint...' : 'Afegir línia'}
              </Button>
              {budgetLines.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-[11px]"
                  onClick={handleReimportBudgetLines}
                  disabled={reimportingBudgetLines}
                  title="Torna a copiar les línies des del pressupost actual — útil si s'ha editat després d'acceptar-se. ATENCIÓ: sobreescriu les edicions manuals fetes aquí."
                >
                  <RefreshCw className={`w-3 h-3 mr-1 ${reimportingBudgetLines ? 'animate-spin' : ''}`} />
                  {reimportingBudgetLines ? 'Reimportant...' : 'Reimportar del pressupost'}
                </Button>
              )}
              {budgetLines.length > 0 && (
                <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={handleExportDetallExcel}>
                  <Download className="w-3 h-3 mr-1" />
                  Excel
                </Button>
              )}
            </div>
          </div>
          {budgetLines.length === 0 ? (
            <p className="text-sm text-muted-foreground p-5 italic">
              Encara no hi ha cap línia. Fes servir "Afegir línia" per començar.
            </p>
          ) : (
          <>
          {/* Capçalera de la fila de línia — #, Qt, Ut, Concepte, Total cost (del
              desglose), Preu (manual), Preu/U (= Preu/Qt), Resultat (= Preu -
              Total cost) i Marge % (= Resultat/Preu). */}
          <div className="flex items-start gap-2 px-4 pt-3 pb-1 text-muted-foreground font-medium" style={{ fontSize: '10px' }}>
            <span className="shrink-0" style={{ width: '36px' }}></span>
            <div className="flex-1 grid gap-2" style={{ gridTemplateColumns: '28px 56px 50px 1fr 80px 72px 64px 80px 56px' }}>
              <span>#</span>
              <span className="text-right">Qt</span>
              <span>Ut</span>
              <span>Concepte</span>
              <span className="text-right">Total cost</span>
              <span className="text-right">Preu</span>
              <span className="text-right">Preu/U</span>
              <span className="text-right">Resultat</span>
              <span className="text-right">Marge %</span>
            </div>
            <span className="shrink-0" style={{ width: '150px' }}></span>
          </div>
          <div className="divide-y divide-gray-100">
            {budgetLines.map((line, lineIdx) => {
              const isExpanded = expandedBudgetLines.has(line.id);
              const draft = effectiveBudgetLine(line);
              const isDirty = budgetLineDrafts[line.id] !== undefined;
              const draftPreuPerQt = Number(draft.preuUT || 0) / (Number(draft.medicio || 0) || 1);
              // Sempre calculat en viu a partir del desglose actual — no es pot confiar en
              // draft.totalCost perque les línies importades del pressupost mai han passat per
              // un onCommit d'edició que l'hagi omplert (el valor guardat a BD es queda a 0).
              const draftTotalCost = recalcTotalCostFromDesglose(line, draft.escandallLines ?? []);
              const draftPreu = Number(draft.preuUT || 0);
              const draftResultat = draftPreu - draftTotalCost;
              const draftMargePct = draftPreu > 0 ? (draftResultat / draftPreu) * 100 : 0;
              return (
              <div
                key={line.id}
                className={`p-4 ${dropLineTarget === lineIdx ? 'border-t-2 border-teal-400' : ''} ${dragLineFrom === lineIdx ? 'opacity-40' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDropLineTarget(lineIdx); }}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropLineTarget(null); }}
                onDrop={() => handleLineDrop(lineIdx)}
              >
                <div className="flex items-start gap-2">
                  <span
                    draggable
                    onDragStart={() => handleLineDragStart(lineIdx)}
                    onDragEnd={handleLineDragEnd}
                    className="shrink-0 mt-0.5 cursor-grab text-gray-300 hover:text-gray-500"
                    title="Arrossega per reordenar"
                  >
                    <GripVertical size={14} />
                  </span>
                  <button type="button" onClick={() => toggleBudgetLine(line.id)} className="shrink-0 mt-0.5" title={draft.descripcioLlarga || undefined}>
                    {isExpanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                  </button>
                  <div className="flex-1 grid gap-2 items-center" style={{ fontSize: '10px', gridTemplateColumns: '28px 56px 50px 1fr 80px 72px 64px 80px 56px' }}>
                    <span className="text-muted-foreground">{draft.ordre ?? '—'}</span>
                    <NumInput
                      className="w-full h-5 text-right border border-gray-200 rounded px-1 py-0.5"
                      style={{ fontSize: '10px' }}
                      value={draft.medicio}
                      onCommit={(n) => updateBudgetLineDraft(line, { medicio: n })}
                    />
                    <select
                      className="w-full border border-gray-200 rounded px-1 py-0.5"
                      value={draft.tipus || draft.unitat || ''}
                      onChange={(e) => updateBudgetLineDraft(line, { tipus: e.target.value })}
                    >
                      {(draft.tipus || draft.unitat) && !unitTypes.some((ut) => ut.code === (draft.tipus || draft.unitat)) && (
                        <option value={draft.tipus || draft.unitat}>{draft.tipus || draft.unitat}</option>
                      )}
                      {unitTypes.length > 0
                        ? unitTypes.map((ut) => <option key={ut.code} value={ut.code}>{ut.code}</option>)
                        : <option value="Ut">Ut</option>}
                    </select>
                    <div className="min-w-0">
                      <input
                        type="text" className="w-full border border-gray-200 rounded px-1 py-0.5 font-medium"
                        value={draft.concepte}
                        onChange={(e) => updateBudgetLineDraft(line, { concepte: e.target.value })}
                      />
                      {draft.nodeName && (
                        <span className="text-muted-foreground ml-1">
                          {draft.nodeCode ? `${draft.nodeCode} — ` : ''}{draft.nodeName}
                        </span>
                      )}
                    </div>
                    <span className="text-right" title="Suma del Cost total del desglose">{formatNumber(draftTotalCost)} €</span>
                    <NumInput
                      className="w-full h-5 text-right border border-gray-200 rounded px-1 py-0.5"
                      style={{ fontSize: '10px' }}
                      value={draft.preuUT}
                      onCommit={(n) => updateBudgetLineDraft(line, { preuUT: n })}
                    />
                    <span className="font-medium text-right" title="Preu / Qt">{formatNumber(draftPreuPerQt)} €</span>
                    <span className={`text-right ${draftResultat < 0 ? 'text-red-600' : 'text-emerald-700'}`} title="Preu - Total cost">
                      {formatNumber(draftResultat)} €
                    </span>
                    <span className={`text-right ${draftResultat < 0 ? 'text-red-600' : 'text-emerald-700'}`} title="Resultat / Preu">
                      {formatNumber(draftMargePct)}%
                    </span>
                  </div>
                  <div className="flex items-center justify-end gap-1 shrink-0" style={{ width: '150px' }}>
                    {isDirty && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleSaveBudgetLine(line)}
                          disabled={savingBudgetLineId === line.id}
                          className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 whitespace-nowrap"
                        >
                          {savingBudgetLineId === line.id ? 'Desant...' : 'Guardar'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCancelBudgetLineDraft(line.id)}
                          className="text-[10px] font-medium px-1.5 py-0.5 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 whitespace-nowrap"
                        >
                          Desfer
                        </button>
                      </>
                    )}
                    <button type="button" onClick={() => handleDeleteBudgetLine(line)} className="text-gray-400 hover:text-red-600 p-1 shrink-0" title="Eliminar línia">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {isExpanded && (
                  <div className="overflow-x-auto mt-2">
                    <table className="escandall-lines-table text-muted-foreground" style={{ width: '100%', tableLayout: 'auto', fontSize: '9px', lineHeight: '1.2' }}>
                      <colgroup>
                        <col style={{ width: '20px' }} />
                        <col style={{ width: '22px' }} />
                        <col style={{ width: '85px' }} />
                        <col style={{ width: '48px' }} />
                        <col style={{ width: '230px' }} />
                        <col style={{ width: '230px' }} />
                        <col style={{ width: '20px' }} />
                        <col style={{ width: '230px' }} />
                        <col style={{ width: '52px' }} />
                        <col style={{ width: '52px' }} />
                        <col style={{ width: '52px' }} />
                        <col style={{ width: '52px' }} />
                        <col style={{ width: '52px' }} />
                        <col style={{ width: '62px' }} />
                        <col style={{ width: '24px' }} />
                        <col style={{ width: '70px' }} />
                        <col style={{ width: '70px' }} />
                        <col style={{ width: '58px' }} />
                        <col style={{ width: '48px' }} />
                        <col style={{ width: '120px' }} />
                      </colgroup>
                      <thead className="bg-slate-200">
                        <tr>
                          <th />
                          <th className="px-1 py-0.5 text-left">#</th>
                          <th className="px-1 py-0.5 text-left">Cap.</th>
                          <th className="px-1 py-0.5 text-left">Ref. Plànol</th>
                          <th className="px-1 py-0.5 text-left">Descripció</th>
                          <th className="px-1 py-0.5 text-left">Operació</th>
                          <th className="px-0.5 py-0.5 text-center" title="Imatge tècnica"></th>
                          <th className="px-1 py-0.5 text-left">Element</th>
                          <th className="px-1 py-0.5 text-center" title="Tipus d'element">Tipus</th>
                          <th className="px-1 py-0.5 text-center">UA</th>
                          <th className="px-1 py-0.5 text-right">Q.</th>
                          <th className="px-1 py-0.5 text-right">Mida A</th>
                          <th className="px-1 py-0.5 text-right">Mida B</th>
                          <th className="px-1 py-0.5 text-right">Mida C</th>
                          <th className="px-1 py-0.5 text-right">Amidament</th>
                          <th className="px-1 py-0.5 text-center" title="Preu bloquejat">🔒</th>
                          <th className="px-1 py-0.5 text-right">Cost/U</th>
                          <th className="px-1 py-0.5 text-right">Cost total</th>
                          <th className="px-1 py-0.5 text-right" title="Pes total de la línia (Pes/ut × Amidament)">Pes</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {groupByChapter((draft.escandallLines ?? []).map((sub, i) => ({ ...sub, __i: i }))).sort((a, b) => {
                          if (a.chapter === SENSE_CHAPTER) return 1;
                          if (b.chapter === SENSE_CHAPTER) return -1;
                          return 0;
                        }).map((group) => (
                          <Fragment key={group.chapter}>
                            <tr className="border-t border-slate-300 bg-slate-50">
                              <td className="px-0.5 py-0 text-center">
                                {(() => {
                                  const selectableIndexes = group.items
                                    .map(({ item }) => (item as EscandallDetailLine & { __i: number }).__i);
                                  if (selectableIndexes.length === 0) return null;
                                  return (
                                    <input
                                      type="checkbox"
                                      disabled={isDirty}
                                      style={{ width: '12px', height: '12px' }}
                                      checked={selectableIndexes.every((idx) => selectedSublines.has(sublineKey(line.id, idx)))}
                                      onChange={(e) => {
                                        setSelectedSublines((prev) => {
                                          const next = new Set(prev);
                                          for (const idx of selectableIndexes) {
                                            const key = sublineKey(line.id, idx);
                                            if (e.target.checked) next.add(key); else next.delete(key);
                                          }
                                          return next;
                                        });
                                      }}
                                      title={`Seleccionar tot el capítol "${group.chapter}"`}
                                    />
                                  );
                                })()}
                              </td>
                              <td colSpan={19} className="px-2 py-1.5">
                                <span className="font-semibold text-slate-600" style={{ fontSize: '9px' }}>{group.chapter}</span>
                              </td>
                            </tr>
                            {group.items.map(({ item: sub, originalIdx }) => {
                              const index = originalIdx;
                              const { measurement, costU, costTotal, pes } = computeSublineCalc(sub, line);
                              const matchingOTs = workOrders.filter((ot) =>
                                (ot.projectBudgetLineId === line.id && ot.budgetLineIndex === index) ||
                                (Array.isArray(ot.groupedBudgetLines) ? ot.groupedBudgetLines : []).some((g) => g.projectBudgetLineId === line.id && g.budgetLineIndex === index)
                              );
                              // Bloqueig d'edicio de linies amb OT desactivat temporalment mentre el client
                              // experimenta (2026-10-07) — abans: `index <= maxOtIndex` (bloquejava tota
                              // linia igual o anterior a la OT d'index mes alt del capitol).
                              const locked = false;
                              const badge = badgeFor((sub.childType as any) || 'MATERIAL', sub.nature || 'MATERIAL');
                              const sKey = sublineKey(line.id, index);
                              return (
                                <tr
                                  key={index}
                                  className={[
                                    'border-b',
                                    locked ? 'bg-amber-50/40' : 'hover:bg-slate-50',
                                    dragSublineFrom?.lineId === line.id && dragSublineFrom.index === index ? 'opacity-40' : '',
                                    dropSublineTarget === sKey ? 'border-t-2 border-teal-400' : '',
                                  ].filter(Boolean).join(' ')}
                                  onDragOver={(e) => { if (!locked) { e.preventDefault(); setDropSublineTarget(sKey); } }}
                                  onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropSublineTarget(null); }}
                                  onDrop={() => { if (!locked) handleSublineDrop(line, index); }}
                                >
                                  <td className="px-0.5 py-0 text-center">
                                    <input
                                      type="checkbox"
                                      disabled={isDirty}
                                      style={{ width: '12px', height: '12px' }}
                                      checked={selectedSublines.has(sublineKey(line.id, index))}
                                      onChange={() => toggleSublineSelection(line.id, index)}
                                      title={matchingOTs.length > 0 ? 'Aquesta partida ja té una OT assignada' : undefined}
                                    />
                                  </td>
                                  <td className="px-1 py-0.5 text-muted-foreground">
                                    <span className="inline-flex items-center gap-0.5">
                                      {!locked && (
                                        <span
                                          draggable
                                          onDragStart={() => handleSublineDragStart(line.id, index)}
                                          onDragEnd={handleSublineDragEnd}
                                          className="cursor-grab text-slate-300 hover:text-slate-500"
                                          title="Arrossega per reordenar"
                                        >
                                          <GripVertical size={10} />
                                        </span>
                                      )}
                                      {index + 1}
                                    </span>
                                  </td>
                                  <td className="px-0.5 py-0.5">
                                    <Input
                                      disabled={locked}
                                      value={capitolDrafts[sublineKey(line.id, index)] ?? (sub.chapter || '')}
                                      onFocus={() => setCapitolDrafts((p) => ({ ...p, [sublineKey(line.id, index)]: sub.chapter || '' }))}
                                      onChange={(e) => setCapitolDrafts((p) => ({ ...p, [sublineKey(line.id, index)]: e.target.value }))}
                                      onBlur={(e) => {
                                        updateSublineDraft(line, index, { chapter: e.target.value.trim() });
                                        setCapitolDrafts((p) => { const n = { ...p }; delete n[sublineKey(line.id, index)]; return n; });
                                      }}
                                      className="w-full h-5" style={{ fontSize: '9px', padding: '0 2px' }} />
                                  </td>
                                  <td className="px-0.5 py-0.5">
                                    <Input disabled={locked} value={sub.referencia || ''} onChange={(e) => updateSublineDraft(line, index, { referencia: e.target.value })}
                                      className="w-full h-5" style={{ fontSize: '9px', padding: '0 4px' }} />
                                  </td>
                                  <td className="px-0.5 py-0.5">
                                    <Input disabled={locked} value={sub.detail || ''} onChange={(e) => updateSublineDraft(line, index, { detail: e.target.value })}
                                      className="w-full h-5" style={{ fontSize: '9px', padding: '0 4px' }} placeholder="Nota…" />
                                  </td>
                                  <td className="px-0.5 py-0.5">
                                    <Input disabled={locked} value={sub.observations || ''} onChange={(e) => updateSublineDraft(line, index, { observations: e.target.value })}
                                      className="w-full h-5" style={{ fontSize: '9px', padding: '0 4px' }} placeholder="Operació…" />
                                  </td>
                                  <td className="px-0 py-0 text-center">
                                    {sub.elementImageUrl && (
                                      <img src={sub.elementImageUrl} alt="" style={{ width: '18px', height: '18px', objectFit: 'cover', borderRadius: '2px', display: 'inline-block' }} />
                                    )}
                                  </td>
                                  <td className="px-0.5 py-0.5">
                                    <div className="flex items-center gap-0.5">
                                      <MaterialNodeSearchInput
                                        disabled={locked}
                                        value={sub.description || ''}
                                        className="w-full h-5"
                                        inputStyle={{ fontSize: '9px', padding: '0 4px' }}
                                        onChangeText={(text) => updateSublineDraft(line, index, { description: text })}
                                        onSelectMaterial={(mat, nature) => applyMaterialToSubline(line, index, sub.nature, mat, nature)}
                                        onSelectNode={(node) => applyNodeToSubline(line, index, node)}
                                      />
                                      {!locked && (
                                        <button
                                          type="button"
                                          title="Cercar material o escandall (modal gran)"
                                          onMouseDown={(e) => { e.preventDefault(); setElementPickerFor({ lineId: line.id, index }); }}
                                          className="shrink-0 p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                                        >
                                          ⬡
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-0.5 py-0.5 text-center">
                                    {sub.childType === 'NODE' ? (
                                      <span className={`inline-block text-[9px] font-bold px-0.5 rounded leading-3 ${badge.cls}`}>{badge.text}</span>
                                    ) : (
                                      <select
                                        disabled={locked}
                                        value={sub.nature || 'MATERIAL'}
                                        onChange={(e) => updateSublineDraft(line, index, { nature: e.target.value })}
                                        title="Naturalesa de la línia"
                                        className={`text-[9px] font-bold px-0.5 rounded leading-3 cursor-pointer border-0 outline-none appearance-none ${badge.cls}`}
                                        style={{ fontSize: '9px', padding: '0 2px', height: '13px' }}
                                      >
                                        <option value="MATERIAL">MT</option>
                                        <option value="MO_PROPIA" style={{ color: '#6d28d9', backgroundColor: '#ede9fe' }}>MO</option>
                                        <option value="MO_SUBCONTRACT" style={{ color: '#be123c', backgroundColor: '#ffe4e6' }}>MO</option>
                                        <option value="SERVEIS" style={{ color: '#b45309', backgroundColor: '#fef3c7' }}>SV</option>
                                        <option value="ALTRES" style={{ color: '#c2410c', backgroundColor: '#ffedd5' }}>AL</option>
                                      </select>
                                    )}
                                  </td>
                                  <td className="px-0.5 py-0.5">
                                    <select disabled={locked} value={sub.unit || 'Ut'} onChange={(e) => updateSublineDraft(line, index, { unit: e.target.value })}
                                      className="w-full border rounded h-5" style={{ fontSize: '9px', padding: '0 2px' }}>
                                      {unitTypes.length > 0
                                        ? unitTypes.map((ut) => <option key={ut.code} value={ut.code}>{ut.code}</option>)
                                        : <option value="Ut">Ut</option>}
                                    </select>
                                  </td>
                                  <td className="px-0.5 py-0.5">
                                    <NumInput disabled={locked} value={sub.quantity}
                                      onCommit={(n) => updateSublineDraft(line, index, { quantity: n })}
                                      className="w-full h-5 text-right" style={{ fontSize: '9px', padding: '0 2px' }} />
                                  </td>
                                  <td className="px-0.5 py-0.5">
                                    <NumInput decimals={3} disabled={locked} value={sub.dimensionA}
                                      onCommit={(n) => updateSublineDraft(line, index, { dimensionA: n })}
                                      className="w-full h-5 text-right" style={{ fontSize: '9px', padding: '0 2px' }} />
                                  </td>
                                  <td className="px-0.5 py-0.5">
                                    <NumInput decimals={3} disabled={locked} value={sub.dimensionB}
                                      onCommit={(n) => updateSublineDraft(line, index, { dimensionB: n })}
                                      className="w-full h-5 text-right" style={{ fontSize: '9px', padding: '0 2px' }} />
                                  </td>
                                  <td className="px-0.5 py-0.5">
                                    <NumInput decimals={3} disabled={locked} value={sub.dimensionH}
                                      onCommit={(n) => updateSublineDraft(line, index, { dimensionH: n })}
                                      className="w-full h-5 text-right" style={{ fontSize: '9px', padding: '0 2px' }} />
                                  </td>
                                  <td className="px-1 py-0.5 text-right bg-slate-50 whitespace-nowrap">{formatNumber(measurement)}</td>
                                  <td className="px-0.5 py-0.5 text-center">
                                    <input type="checkbox" disabled={locked} checked={!!sub.isPriceLocked}
                                      onChange={(e) => updateSublineDraft(line, index, { isPriceLocked: e.target.checked })}
                                      style={{ width: '12px', height: '12px' }} />
                                  </td>
                                  <td className="px-0.5 py-0.5">
                                    <NumInput disabled={locked} value={costU}
                                      onCommit={(n) => updateSublineDraft(line, index, { resolvedUnitCost: n })}
                                      className="w-full h-5 text-right" style={{ fontSize: '9px', padding: '0 4px' }} />
                                  </td>
                                  <td className="px-1 py-0.5 text-right font-medium bg-slate-50 whitespace-nowrap">{formatNumber(costTotal)}</td>
                                  <td className="px-0.5 py-0.5">
                                    <NumInput
                                      disabled={locked} value={pes}
                                      onCommit={(n) => updateSublineDraft(line, index, { pesTotal: n })}
                                      className="w-full h-5 text-right" style={{ fontSize: '9px', padding: '0 2px' }} />
                                  </td>
                                  <td className="px-0.5 py-0.5 text-center">
                                    <div className="flex items-center justify-center gap-1">
                                      {matchingOTs.length > 0 && (
                                        <span
                                          className="inline-flex items-center px-1 rounded-full text-[9px] font-medium bg-indigo-50 text-indigo-700 whitespace-nowrap"
                                          title={matchingOTs.map((ot) => `${ot.orderNumber} — ${ot.name}`).join('\n')}
                                        >
                                          {matchingOTs.map((ot) => ot.orderNumber).join(', ')}
                                        </span>
                                      )}
                                      {locked ? (
                                        <span title="Aquesta línia ja té OT's creades — no es pot eliminar" className="text-slate-300 text-[10px]">🔒</span>
                                      ) : (
                                        <button type="button" onClick={() => removeSublineDraft(line, index)} className="text-slate-400 hover:text-red-600" title="Eliminar línia del desglose">
                                          <Trash2 className="h-3 w-3" />
                                        </button>
                                      )}
                                      {matchingOTs.length === 0 && (
                                        <button
                                          type="button"
                                          disabled={isDirty}
                                          title={isDirty ? "Desa els canvis abans de crear una OT" : 'Crear OT'}
                                          onClick={() => handleCreateOTForSubline(line, sub, index)}
                                          className="text-[9px] font-medium px-1 py-0.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                                        >
                                          +OT
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                            {/* Subtotal capítol */}
                            <tr className="bg-slate-50 border-b border-slate-300" style={{ fontSize: '9px', height: '24px' }}>
                              <td colSpan={17} className="px-1 py-0.5 text-right text-muted-foreground">Subtotal capítol:</td>
                              <td className="px-1 py-0.5 text-right whitespace-nowrap">
                                {formatNumber(group.items.reduce((s, { item }) => s + computeSublineCalc(item, line).costTotal, 0))}
                              </td>
                              <td className="px-1 py-0.5 text-right whitespace-nowrap">
                                {formatNumber(group.items.reduce((s, { item }) => s + computeSublineCalc(item, line).pes, 0))}
                              </td>
                              <td className="px-0.5 py-0.5 text-center">
                                <button type="button" onClick={() => addSublineDraft(line, group.chapter === SENSE_CHAPTER ? '' : group.chapter)} className="text-indigo-500 hover:text-indigo-700" title={`Afegir línia a "${group.chapter}"`}>
                                  <Plus className="h-3 w-3" />
                                </button>
                              </td>
                            </tr>
                          </Fragment>
                        ))}
                      </tbody>
                      <tfoot className="bg-slate-100 font-medium" style={{ fontSize: '9px' }}>
                        <tr style={{ height: '24px' }}>
                          <td colSpan={17} className="px-1 py-0.5 text-right">Totals:</td>
                          <td className="px-1 py-0.5 text-right whitespace-nowrap">
                            {formatNumber((draft.escandallLines ?? []).reduce((s, sub) => s + computeSublineCalc(sub, line).costTotal, 0))}
                          </td>
                          <td className="px-1 py-0.5 text-right whitespace-nowrap">
                            {formatNumber((draft.escandallLines ?? []).reduce((s, sub) => s + computeSublineCalc(sub, line).pes, 0))}
                          </td>
                          <td className="px-0.5 py-0.5 text-center">
                            <button type="button" onClick={() => addSublineDraft(line)} className="text-indigo-600 hover:text-indigo-800" title="Afegir línia nova">
                              <Plus className="h-3 w-3" />
                            </button>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
              );
            })}
          </div>
          {(() => {
            const totals = budgetLines.reduce((acc, line) => {
              const draft = effectiveBudgetLine(line);
              const totalCost = recalcTotalCostFromDesglose(line, draft.escandallLines ?? []);
              const preu = Number(draft.preuUT || 0);
              return { totalCost: acc.totalCost + totalCost, preu: acc.preu + preu };
            }, { totalCost: 0, preu: 0 });
            const resultat = totals.preu - totals.totalCost;
            const margePct = totals.preu > 0 ? (resultat / totals.preu) * 100 : 0;
            return (
              <div className="flex items-start gap-2 px-4 py-2.5 border-t-2 border-gray-200 bg-gray-50 font-semibold" style={{ fontSize: '10px' }}>
                <span className="shrink-0" style={{ width: '36px' }}></span>
                <div className="flex-1 grid gap-2 items-center" style={{ gridTemplateColumns: '28px 56px 50px 1fr 80px 72px 64px 80px 56px' }}>
                  <span />
                  <span />
                  <span />
                  <span className="text-right">Totals:</span>
                  <span className="text-right" title="Suma del Total cost de totes les línies">{formatNumber(totals.totalCost)} €</span>
                  <span className="text-right" title="Suma del Preu de totes les línies">{formatNumber(totals.preu)} €</span>
                  <span />
                  <span className={`text-right ${resultat < 0 ? 'text-red-600' : 'text-emerald-700'}`} title="Preu - Total cost">
                    {formatNumber(resultat)} €
                  </span>
                  <span className={`text-right ${resultat < 0 ? 'text-red-600' : 'text-emerald-700'}`} title="Resultat / Preu">
                    {formatNumber(margePct)}%
                  </span>
                </div>
                <span className="shrink-0" style={{ width: '150px' }}></span>
              </div>
            );
          })()}
          </>
          )}
        </div>
      )}

      {!isNew && tab === 'cronograma' && project && (() => {
        const chapterGroups = buildCronoChapters();
        const effectiveRangeStart = cronoRangeStart || project.startDate || toISO(new Date());
        const todayIso = toISO(new Date());
        const CRONO_CHAPTER_COLORS = [
          'bg-slate-200', 'bg-lime-200', 'bg-orange-200', 'bg-sky-200', 'bg-stone-300',
          'bg-emerald-200', 'bg-red-200', 'bg-teal-200', 'bg-violet-200', 'bg-amber-200',
        ];
        const chapterRange = (group: ReturnType<typeof buildCronoChapters>[number]) => {
          const segs = group.items.flatMap(({ item }) => cronoItemSegments(item));
          if (segs.length === 0) return null;
          return { start: segs.map((s) => s.startDate).sort()[0], end: segs.map((s) => s.endDate).sort().slice(-1)[0] };
        };
        const barStyle = (start: string, end: string) => {
          const offsetDays = diffDaysISO(effectiveRangeStart, start);
          const widthDays = diffDaysISO(start, end) + 1;
          return { left: `${offsetDays * CRONO_DAY_PX}px`, width: `${Math.max(widthDays, 1) * CRONO_DAY_PX}px` };
        };
        // Agrupa els CRONO_VISIBLE_DAYS dies visibles per mes, per a la fila
        // de capçalera superior (equivalent a "Setmana N" pero amb mesos,
        // ja que ara cada dia te la seva pròpia columna).
        const monthGroups: { label: string; days: number }[] = [];
        for (let i = 0; i < CRONO_VISIBLE_DAYS; i++) {
          const d = new Date(addDaysISO(effectiveRangeStart, i) + 'T00:00:00');
          const label = `${CRONO_MONTH_LABELS[d.getMonth()]} ${d.getFullYear()}`;
          if (monthGroups.length > 0 && monthGroups[monthGroups.length - 1].label === label) {
            monthGroups[monthGroups.length - 1].days += 1;
          } else {
            monthGroups.push({ label, days: 1 });
          }
        }
        const DayGridLines = () => (
          <div className="absolute inset-0 flex pointer-events-none" style={{ width: CRONO_GRID_WIDTH }}>
            {Array.from({ length: CRONO_VISIBLE_DAYS }).map((_, di) => {
              const dayIso = addDaysISO(effectiveRangeStart, di);
              const d = new Date(dayIso + 'T00:00:00');
              const isWeekend = d.getDay() === 0 || d.getDay() === 6;
              const isToday = dayIso === todayIso;
              return (
                <div
                  key={di}
                  className={`shrink-0 border-r ${isToday ? 'bg-amber-50 border-amber-200' : `border-gray-100 ${isWeekend ? 'bg-gray-50' : ''}`}`}
                  style={{ width: CRONO_DAY_PX }}
                />
              );
            })}
          </div>
        );

        const totalDaysOfCronoLine = (line: CronogramaLine) =>
          line.segments.reduce((s, seg) => s + diffDaysISO(seg.startDate, seg.endDate) + 1, 0);

        // Fila de barres d'una línia de planificació (sense cost) — compartida
        // entre les que s'injecten dins un capítol (real o virtual) i les de
        // la llista plana "Línies de planificació", totes fan servir els
        // mateixos handlers (handleAddCronoSegment, etc.).
        const CronoLineBarsRow = ({ line }: { line: CronogramaLine }) => (
          <div className="relative h-8 border-b border-gray-100" style={{ width: CRONO_GRID_WIDTH }}>
            <DayGridLines />
            {line.segments.length === 0 && (
              <button
                type="button"
                onClick={() => handleAddCronoSegment(line.id)}
                className="absolute left-1 top-1 bottom-1 px-2 rounded border border-dashed border-gray-300 text-[10px] text-muted-foreground hover:border-teal-400 hover:text-teal-600"
              >
                + Planificar
              </button>
            )}
            {line.segments.map((seg) => {
              const moving = customCronoMoving && customCronoMoving.lineId === line.id && customCronoMoving.segmentId === seg.id ? customCronoMoving : null;
              const resizing = customCronoResizing && customCronoResizing.lineId === line.id && customCronoResizing.segmentId === seg.id ? customCronoResizing : null;
              const start = moving?.previewStart ?? seg.startDate;
              const end = resizing?.previewEnd ?? moving?.previewEnd ?? seg.endDate;
              return (
                <div
                  key={seg.id}
                  className="absolute top-1 bottom-1 rounded bg-indigo-500 hover:bg-indigo-600 transition-colors cursor-move flex items-center group"
                  style={barStyle(start, end)}
                  onMouseDown={(e) => handleCustomCronoMoveMouseDown(e, line.id, seg.id, seg.startDate, seg.endDate)}
                  title={`${line.name}: ${start} — ${end}`}
                >
                  <button
                    type="button"
                    title="Eliminar aquest dia de planificació"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); handleDeleteCronoSegment(line.id, seg.id); }}
                    className="opacity-0 group-hover:opacity-100 absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-white border border-gray-300 text-gray-500 hover:text-red-600 hover:border-red-400 flex items-center justify-center text-[9px] leading-none"
                  >
                    ×
                  </button>
                  <div
                    className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-black/20"
                    onMouseDown={(e) => handleCustomCronoResizeMouseDown(e, line.id, seg.id, seg.startDate, seg.endDate)}
                  />
                </div>
              );
            })}
          </div>
        );

        // Línies de planificació (sense cost) agrupades per chapterTag — les
        // que coincideixen amb un capítol real s'injecten dins el seu grup;
        // la resta formen "capítols virtuals" (creats nomes per planificar).
        const realChapterNames = new Set(chapterGroups.map((g) => g.chapter));
        const taggedByChapter = (chapter: string) => cronoLines.filter((l) => l.chapterTag === chapter);
        const untaggedCronoLines = cronoLines.filter((l) => !l.chapterTag);
        const virtualChapterTags: string[] = [];
        cronoLines.forEach((l) => {
          if (l.chapterTag && !realChapterNames.has(l.chapterTag) && !virtualChapterTags.includes(l.chapterTag)) {
            virtualChapterTags.push(l.chapterTag);
          }
        });

        return (
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="px-5 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <CalendarRange size={16} className="text-amber-600" /> Cronograma
                {cronoLineSaving && <span className="text-[10px] font-normal text-muted-foreground">(desant...)</span>}
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {new Date(effectiveRangeStart + 'T00:00:00').toLocaleDateString('ca-ES')} — {new Date(addDaysISO(effectiveRangeStart, CRONO_VISIBLE_DAYS - 1) + 'T00:00:00').toLocaleDateString('ca-ES')}
                </span>
                <Button variant="outline" size="sm" onClick={() => setCronoRangeStart(addDaysISO(effectiveRangeStart, -7))}>‹ Setmana anterior</Button>
                <Button variant="outline" size="sm" onClick={() => setCronoRangeStart(addDaysISO(effectiveRangeStart, 7))}>Setmana següent ›</Button>
                <Button variant="outline" size="sm" onClick={handleExportCronograma} disabled={exportingCrono}>
                  <Download size={14} className="mr-1" /> {exportingCrono ? 'Exportant...' : 'Exportar a Excel'}
                </Button>
                <Button size="sm" onClick={() => { setCronoImportSelection(new Set()); setShowCronoImportPicker(true); }}>
                  <Plus size={14} className="mr-1" /> Importar línies
                </Button>
              </div>
            </div>

            {chapterGroups.length === 0 ? (
              <p className="px-5 py-8 text-xs text-muted-foreground text-center">
                Encara no has importat cap línia del desglose. Fes clic a "Importar línies" per triar quines partides vols planificar.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <div className="flex" style={{ minWidth: `${480 + CRONO_GRID_WIDTH}px` }}>
                  {/* Columna esquerra: POS / Descripció / Tipus de recurs / Dies */}
                  <div className="w-[480px] shrink-0 border-r border-gray-200">
                    <div className="flex items-center text-[10px] font-semibold text-muted-foreground uppercase tracking-wide border-b border-gray-200 bg-gray-50 h-10">
                      <span className="w-12 px-2 shrink-0">Pos</span>
                      <span className="flex-1 px-2">Descripció</span>
                      <span className="w-12 px-1 shrink-0 text-center">Hores</span>
                      <span className="w-14 px-2 shrink-0 text-center">Dies</span>
                    </div>
                    {chapterGroups.map((group, gi) => (
                      <Fragment key={group.chapter}>
                        <div
                          draggable={group.chapter !== SENSE_CHAPTER}
                          onDragStart={() => group.chapter !== SENSE_CHAPTER && setCronoChapterDragFrom(group.chapter)}
                          onDragOver={(e) => { e.preventDefault(); if (group.chapter !== SENSE_CHAPTER) setCronoChapterDropTarget(group.chapter); }}
                          onDrop={() => handleCronoChapterDrop(group.chapter)}
                          onDragEnd={() => { setCronoChapterDragFrom(null); setCronoChapterDropTarget(null); }}
                          className={`flex items-center gap-1 h-8 px-2 border-b border-gray-200 ${CRONO_CHAPTER_COLORS[gi % CRONO_CHAPTER_COLORS.length]} ${cronoChapterDropTarget === group.chapter ? 'ring-2 ring-inset ring-amber-400' : ''}`}
                        >
                          {group.chapter !== SENSE_CHAPTER && <GripVertical size={12} className="text-gray-500/60 cursor-grab shrink-0" />}
                          <span className="text-xs font-semibold text-gray-700 shrink-0">{gi + 1}.</span>
                          {group.chapter === SENSE_CHAPTER ? (
                            <span className="text-xs font-semibold text-gray-700 truncate flex-1">Sense capítol</span>
                          ) : (
                            <Input
                              value={cronoChapterNameDrafts[group.chapter] ?? group.chapter}
                              onChange={(e) => setCronoChapterNameDrafts((p) => ({ ...p, [group.chapter]: e.target.value }))}
                              onBlur={(e) => {
                                const val = e.target.value;
                                setCronoChapterNameDrafts((p) => { const n = { ...p }; delete n[group.chapter]; return n; });
                                if (val.trim() && val !== group.chapter) handleRenameCronoChapter(group.chapter, val);
                              }}
                              disabled={savingCronoChapter === group.chapter}
                              className="h-6 text-xs font-semibold text-gray-700 bg-white/60 flex-1 min-w-0 px-1.5"
                            />
                          )}
                          <button
                            type="button"
                            title="Afegeix una línia de planificació (sense cost) dins d'aquest capítol"
                            onClick={() => handleAddCronoLine(group.chapter)}
                            disabled={addingCronoLine}
                            className="p-0.5 rounded hover:bg-teal-100 text-teal-700 shrink-0"
                          >
                            <Plus size={12} />
                          </button>
                          {group.chapter !== SENSE_CHAPTER && (
                            <button
                              type="button"
                              title="Treure la planificació d'aquest capítol (no elimina les partides del desglose)"
                              onClick={() => handleClearCronoChapter(group.chapter)}
                              disabled={savingCronoChapter === group.chapter}
                              className="p-0.5 rounded hover:bg-red-100 text-red-600 shrink-0 disabled:opacity-40"
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                        {group.items.map(({ item }, ii) => {
                          const segs = cronoItemSegments(item);
                          const totalDays = segs.reduce((s, seg) => s + diffDaysISO(seg.startDate, seg.endDate) + 1, 0);
                          const isHours = item.nature === 'MO_PROPIA' || item.nature === 'MO_SUBCONTRACT';
                          // Les hores reals són l'amidament (Q × Mida A × Mida B × Mida C),
                          // no la quantitat en brut — mateix càlcul que la columna
                          // "Amidament" de "Línies del Expedient" (escandallLineMeasurement).
                          const itemHours = escandallLineMeasurement(item);
                          return (
                            <div key={`${item.__lineId}-${item.__index}`} className="flex items-center h-8 px-2 border-b border-gray-100 text-xs gap-0.5">
                              <span className="w-10 px-1 shrink-0 text-[11px] text-muted-foreground font-mono">{gi + 1}.{String(ii + 1).padStart(2, '0')}</span>
                              <Input
                                value={item.cronogramaLabel || ''}
                                onChange={(e) => updateCronoItemLabelLocal(item.__lineId, item.__index, e.target.value)}
                                onBlur={(e) => handleCronoItemLabelBlur(item.__lineId, item.__index, e.target.value)}
                                placeholder={item.description || item.detail || item.referencia || '—'}
                                title="Nom propi del Cronograma — no modifica les Línies del Expedient"
                                className="h-6 text-xs px-1.5 flex-1 min-w-0"
                              />
                              <span className="w-12 px-1 shrink-0 text-center text-muted-foreground" title="Hores assignades (amidament)">{isHours && itemHours > 0 ? `${formatNumber(itemHours)}h` : '—'}</span>
                              <span className="w-8 px-1 shrink-0 text-center text-muted-foreground">{totalDays || '—'}</span>
                              <button
                                type="button"
                                title="Afegeix un dia de planificació"
                                onClick={() => handleAddCronoItemSegment(item.__lineId, item.__index, item)}
                                className="p-0.5 rounded hover:bg-teal-50 text-teal-600 shrink-0"
                              >
                                <Plus size={12} />
                              </button>
                              <button
                                type="button"
                                title="Treure del Cronograma (no elimina la partida de cost)"
                                onClick={() => handleRemoveFromCronograma(item.__lineId, item.__index)}
                                className="p-0.5 rounded hover:bg-red-50 text-red-500 shrink-0"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          );
                        })}
                        {taggedByChapter(group.chapter).map((line) => (
                          <div key={line.id} className="flex items-center h-8 px-2 border-b border-gray-100 text-xs gap-0.5 bg-teal-50/30">
                            <span className="w-10 px-1 shrink-0 text-center text-teal-600">●</span>
                            <Input
                              value={line.name}
                              onChange={(e) => handleRenameCronoLineChange(line.id, e.target.value)}
                              onBlur={handleRenameCronoLineBlur}
                              className="h-6 text-xs px-1.5 flex-1 min-w-0"
                            />
                            <span className="w-8 px-1 shrink-0 text-center text-muted-foreground">{totalDaysOfCronoLine(line) || '—'}</span>
                            <button
                              type="button"
                              title="Afegeix un dia de planificació"
                              onClick={() => handleAddCronoSegment(line.id)}
                              className="p-0.5 rounded hover:bg-teal-50 text-teal-600 shrink-0"
                            >
                              <Plus size={12} />
                            </button>
                            <button
                              type="button"
                              title="Eliminar línia"
                              onClick={() => handleDeleteCronoLine(line.id)}
                              className="p-0.5 rounded hover:bg-red-50 text-red-500 shrink-0"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        ))}
                      </Fragment>
                    ))}

                    {/* Capítols virtuals: creats nomes per planificar, sense cap partida de cost */}
                    {virtualChapterTags.map((tag, vi) => (
                      <Fragment key={`virtual-${tag}`}>
                        <div className={`flex items-center gap-1 h-8 px-2 border-b border-gray-200 ${CRONO_CHAPTER_COLORS[(chapterGroups.length + vi) % CRONO_CHAPTER_COLORS.length]}`}>
                          <span className="text-xs font-semibold text-gray-700 shrink-0">{chapterGroups.length + vi + 1}.</span>
                          <Input
                            value={cronoChapterNameDrafts[tag] ?? tag}
                            onChange={(e) => setCronoChapterNameDrafts((p) => ({ ...p, [tag]: e.target.value }))}
                            onBlur={(e) => {
                              const val = e.target.value;
                              setCronoChapterNameDrafts((p) => { const n = { ...p }; delete n[tag]; return n; });
                              handleRenameCronoChapterTag(tag, val);
                            }}
                            className="h-6 text-xs font-semibold text-gray-700 bg-white/60 flex-1 min-w-0 px-1.5"
                          />
                          <button
                            type="button"
                            title="Afegeix una línia de planificació dins d'aquest capítol"
                            onClick={() => handleAddCronoLine(tag)}
                            disabled={addingCronoLine}
                            className="p-0.5 rounded hover:bg-teal-100 text-teal-700 shrink-0"
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                        {taggedByChapter(tag).map((line) => (
                          <div key={line.id} className="flex items-center h-8 px-2 border-b border-gray-100 text-xs gap-0.5 bg-teal-50/30">
                            <span className="w-10 px-1 shrink-0 text-center text-teal-600">●</span>
                            <Input
                              value={line.name}
                              onChange={(e) => handleRenameCronoLineChange(line.id, e.target.value)}
                              onBlur={handleRenameCronoLineBlur}
                              className="h-6 text-xs px-1.5 flex-1 min-w-0"
                            />
                            <span className="w-8 px-1 shrink-0 text-center text-muted-foreground">{totalDaysOfCronoLine(line) || '—'}</span>
                            <button
                              type="button"
                              title="Afegeix un dia de planificació"
                              onClick={() => handleAddCronoSegment(line.id)}
                              className="p-0.5 rounded hover:bg-teal-50 text-teal-600 shrink-0"
                            >
                              <Plus size={12} />
                            </button>
                            <button
                              type="button"
                              title="Eliminar línia"
                              onClick={() => handleDeleteCronoLine(line.id)}
                              className="p-0.5 rounded hover:bg-red-50 text-red-500 shrink-0"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        ))}
                      </Fragment>
                    ))}

                    {newChapterTagDraft ? (
                      <div className="flex items-center gap-1 px-2 py-1.5 bg-blue-50/40 border-b border-gray-200">
                        <Input
                          autoFocus
                          placeholder="Nom del nou capítol..."
                          onKeyDown={(e) => { if (e.key === 'Enter') handleAddNewChapterTag((e.target as HTMLInputElement).value); if (e.key === 'Escape') setNewChapterTagDraft(false); }}
                          onBlur={(e) => handleAddNewChapterTag(e.target.value)}
                          className="h-7 text-xs flex-1"
                        />
                      </div>
                    ) : (
                      <div className="px-2 py-1.5">
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setNewChapterTagDraft(true)}>
                          <Plus size={14} className="mr-1" /> Nou capítol
                        </Button>
                      </div>
                    )}

                    {/* Línies 100% de planificació — sense cap relació amb el detall/costos */}
                    <div className="flex items-center h-7 px-2 border-b border-t-2 border-gray-200 bg-gray-50">
                      <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Línies de planificació</span>
                    </div>
                    {untaggedCronoLines.map((line) => (
                      <div
                        key={line.id}
                        draggable
                        onDragStart={() => handleCronoLineDragStart(line.id)}
                        onDragOver={(e) => { e.preventDefault(); setCronoLineDropTarget(line.id); }}
                        onDrop={() => handleCronoLineDrop(line.id)}
                        onDragEnd={handleCronoLineDragEnd}
                        className={`flex items-center h-8 px-2 border-b border-gray-100 gap-1 ${cronoLineDropTarget === line.id ? 'bg-amber-50' : ''}`}
                      >
                        <GripVertical size={13} className="text-gray-300 cursor-grab shrink-0" />
                        <Input
                          value={line.name}
                          onChange={(e) => handleRenameCronoLineChange(line.id, e.target.value)}
                          onBlur={handleRenameCronoLineBlur}
                          className="h-6 text-xs px-1.5 flex-1 min-w-0"
                        />
                        <span className="w-10 shrink-0 text-center text-[11px] text-muted-foreground">{totalDaysOfCronoLine(line) || '—'}</span>
                        <button
                          type="button"
                          title="Afegeix un dia de planificació"
                          onClick={() => handleAddCronoSegment(line.id)}
                          className="p-1 rounded hover:bg-teal-50 text-teal-600 shrink-0"
                        >
                          <Plus size={13} />
                        </button>
                        <button
                          type="button"
                          title="Eliminar línia"
                          onClick={() => handleDeleteCronoLine(line.id)}
                          className="p-1 rounded hover:bg-red-50 text-red-500 shrink-0"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                    <div className="px-2 py-1.5">
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => handleAddCronoLine()} disabled={addingCronoLine}>
                        <Plus size={14} className="mr-1" /> Nova línia de planificació
                      </Button>
                    </div>
                  </div>

                  {/* Dreta: graella de dies + barres arrossegables */}
                  <div className="shrink-0 relative" style={{ width: CRONO_GRID_WIDTH }}>
                    {/* Capçalera: mesos + número de dia */}
                    <div className="flex h-5 border-b border-gray-200 bg-gray-50" style={{ width: CRONO_GRID_WIDTH }}>
                      {monthGroups.map((mg, mi) => (
                        <div key={mi} className="shrink-0 border-r border-gray-200 flex items-center justify-center text-[10px] font-semibold text-muted-foreground uppercase" style={{ width: mg.days * CRONO_DAY_PX }}>
                          {mg.label}
                        </div>
                      ))}
                    </div>
                    <div className="flex h-5 border-b border-gray-200 bg-gray-50" style={{ width: CRONO_GRID_WIDTH }}>
                      {Array.from({ length: CRONO_VISIBLE_DAYS }).map((_, di) => {
                        const dayIso = addDaysISO(effectiveRangeStart, di);
                        const d = new Date(dayIso + 'T00:00:00');
                        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                        const isToday = dayIso === todayIso;
                        return (
                          <div
                            key={di}
                            className={`shrink-0 border-r flex items-center justify-center text-[9px] font-semibold ${isToday ? 'bg-amber-200 border-amber-300 text-amber-900' : `border-gray-100 font-normal ${isWeekend ? 'bg-gray-100 text-muted-foreground/60' : 'text-muted-foreground'}`}`}
                            style={{ width: CRONO_DAY_PX }}
                          >
                            {d.getDate()}
                          </div>
                        );
                      })}
                    </div>

                    {chapterGroups.map((group, gi) => {
                      const range = chapterRange(group);
                      return (
                        <Fragment key={group.chapter}>
                          <div className="relative h-8 border-b border-gray-200" style={{ width: CRONO_GRID_WIDTH }}>
                            <DayGridLines />
                            {range && (
                              <div
                                className={`absolute top-1 bottom-1 rounded ${CRONO_CHAPTER_COLORS[gi % CRONO_CHAPTER_COLORS.length]} opacity-70`}
                                style={barStyle(range.start, range.end)}
                                title={`${group.chapter}: ${range.start} — ${range.end}`}
                              />
                            )}
                          </div>
                          {group.items.map(({ item }) => {
                            const key = `${item.__lineId}:${item.__index}`;
                            const saving = cronoSavingKey === key;
                            const segs = cronoItemSegments(item);
                            return (
                              <div key={key} className="relative h-8 border-b border-gray-100" style={{ width: CRONO_GRID_WIDTH }}>
                                <DayGridLines />
                                {segs.length === 0 ? (
                                  <button
                                    type="button"
                                    onClick={() => handleAddCronoItemSegment(item.__lineId, item.__index, item)}
                                    className="absolute left-1 top-1 bottom-1 px-2 rounded border border-dashed border-gray-300 text-[10px] text-muted-foreground hover:border-teal-400 hover:text-teal-600"
                                  >
                                    + Planificar
                                  </button>
                                ) : segs.map((seg) => {
                                  const moving = cronoMoving && cronoMoving.lineId === item.__lineId && cronoMoving.index === item.__index && cronoMoving.segmentId === seg.id ? cronoMoving : null;
                                  const resizing = cronoResizing && cronoResizing.lineId === item.__lineId && cronoResizing.index === item.__index && cronoResizing.segmentId === seg.id ? cronoResizing : null;
                                  const start = moving?.previewStart ?? seg.startDate;
                                  const end = resizing?.previewEnd ?? moving?.previewEnd ?? seg.endDate;
                                  return (
                                    <div
                                      key={seg.id}
                                      className={`absolute top-1 bottom-1 rounded bg-teal-500 hover:bg-teal-600 transition-colors cursor-move flex items-center group ${saving ? 'opacity-50' : ''}`}
                                      style={barStyle(start, end)}
                                      onMouseDown={(e) => !saving && handleCronoMoveMouseDown(e, item.__lineId, item.__index, item, seg.id, seg.startDate, seg.endDate)}
                                      title={`${item.cronogramaLabel || item.description || ''}: ${start} — ${end}`}
                                    >
                                      <button
                                        type="button"
                                        title="Eliminar aquest dia de planificació"
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => { e.stopPropagation(); handleDeleteCronoItemSegment(item.__lineId, item.__index, item, seg.id); }}
                                        className="opacity-0 group-hover:opacity-100 absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-white border border-gray-300 text-gray-500 hover:text-red-600 hover:border-red-400 flex items-center justify-center text-[9px] leading-none"
                                      >
                                        ×
                                      </button>
                                      <div
                                        className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-teal-700/50"
                                        onMouseDown={(e) => !saving && handleCronoResizeMouseDown(e, item.__lineId, item.__index, item, seg.id, seg.startDate, seg.endDate)}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                          {taggedByChapter(group.chapter).map((line) => <CronoLineBarsRow key={line.id} line={line} />)}
                        </Fragment>
                      );
                    })}

                    {/* Capítols virtuals — alineats amb la columna esquerra */}
                    {virtualChapterTags.map((tag) => (
                      <Fragment key={`virtual-${tag}`}>
                        <div className="h-8 border-b border-gray-200" style={{ width: CRONO_GRID_WIDTH }} />
                        {taggedByChapter(tag).map((line) => <CronoLineBarsRow key={line.id} line={line} />)}
                      </Fragment>
                    ))}
                    <div className="h-10" style={{ width: CRONO_GRID_WIDTH }} />

                    {/* Línies 100% de planificació — alineades amb la columna esquerra */}
                    <div className="h-7 border-b border-t-2 border-gray-200 bg-gray-50" style={{ width: CRONO_GRID_WIDTH }} />
                    {untaggedCronoLines.map((line) => <CronoLineBarsRow key={line.id} line={line} />)}
                    <div className="h-10" style={{ width: CRONO_GRID_WIDTH }} />
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Modal "Importar línies al Cronograma" */}
      {showCronoImportPicker && (() => {
        const candidates = buildCronoImportCandidates();
        const toggleItem = (key: string) => setCronoImportSelection((prev) => {
          const next = new Set(prev);
          if (next.has(key)) next.delete(key); else next.add(key);
          return next;
        });
        const chapterKeys = (group: ReturnType<typeof buildCronoImportCandidates>[number]) =>
          group.items.map(({ item }) => `${(item as CronoItem).__lineId}:${(item as CronoItem).__index}`);
        const isChapterFullySelected = (group: ReturnType<typeof buildCronoImportCandidates>[number]) =>
          chapterKeys(group).every((k) => cronoImportSelection.has(k));
        const isChapterPartiallySelected = (group: ReturnType<typeof buildCronoImportCandidates>[number]) =>
          chapterKeys(group).some((k) => cronoImportSelection.has(k));
        const toggleChapter = (group: ReturnType<typeof buildCronoImportCandidates>[number]) => {
          const keys = chapterKeys(group);
          const allSelected = isChapterFullySelected(group);
          setCronoImportSelection((prev) => {
            const next = new Set(prev);
            keys.forEach((k) => (allSelected ? next.delete(k) : next.add(k)));
            return next;
          });
        };
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <CalendarRange size={16} className="text-amber-600" /> Importar línies al Cronograma
                </h3>
                <button onClick={() => setShowCronoImportPicker(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {candidates.length === 0 ? (
                  <p className="px-4 py-10 text-center text-xs text-muted-foreground">
                    No hi ha cap línia pendent d'importar — totes les línies del desglose ja estan al Cronograma.
                  </p>
                ) : (
                  candidates.map((group) => (
                    <div key={group.chapter} className="border-b border-gray-100">
                      <label className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isChapterFullySelected(group)}
                          ref={(el) => { if (el) el.indeterminate = !isChapterFullySelected(group) && isChapterPartiallySelected(group); }}
                          onChange={() => toggleChapter(group)}
                        />
                        {group.chapter}
                      </label>
                      {group.items.map(({ item }) => {
                        const ci = item as CronoItem;
                        const key = `${ci.__lineId}:${ci.__index}`;
                        const badge = badgeFor((ci.childType as any) || 'MATERIAL', ci.nature || 'MATERIAL');
                        return (
                          <label key={key} className="flex items-center gap-2 px-4 py-1.5 pl-8 text-xs hover:bg-gray-50 cursor-pointer">
                            <input type="checkbox" checked={cronoImportSelection.has(key)} onChange={() => toggleItem(key)} />
                            <span className={`shrink-0 px-1 rounded text-[9px] font-bold ${badge.cls}`}>{badge.text}</span>
                            <span className="flex-1 truncate">{ci.description || ci.detail || ci.referencia || '—'}</span>
                            {ci.quantity != null && <span className="shrink-0 text-muted-foreground">{ci.quantity} {ci.unit || ''}</span>}
                          </label>
                        );
                      })}
                    </div>
                  ))
                )}
              </div>
              <div className="flex items-center justify-between px-4 py-3 border-t flex-shrink-0">
                <span className="text-xs text-muted-foreground">{cronoImportSelection.size} línia(es) seleccionada(es)</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowCronoImportPicker(false)}>Cancel·lar</Button>
                  <Button size="sm" onClick={handleImportCronoSelection} disabled={cronoImporting || cronoImportSelection.size === 0}>
                    {cronoImporting ? 'Important...' : 'Importar'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal gran de cerca de material/escandall per a una fila del desglose */}
      <MaterialNodeSearchModal
        open={elementPickerFor !== null}
        onClose={() => setElementPickerFor(null)}
        title="Cercar material o escandall"
        onSelectMaterial={(mat, nature) => {
          if (!elementPickerFor) return;
          const line = budgetLines.find((l) => l.id === elementPickerFor.lineId);
          if (!line) return;
          const sub = (effectiveBudgetLine(line).escandallLines ?? [])[elementPickerFor.index];
          applyMaterialToSubline(line, elementPickerFor.index, sub?.nature, mat, nature);
        }}
        onSelectNode={(node) => {
          if (!elementPickerFor) return;
          const line = budgetLines.find((l) => l.id === elementPickerFor.lineId);
          if (!line) return;
          applyNodeToSubline(line, elementPickerFor.index, node);
        }}
      />

      {/* Modal Compres */}
      {showCompresModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Compres — Exp. {project.expedientNumber}
              </h3>
              <button onClick={() => setShowCompresModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loadingCompres ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
              ) : purchases.length === 0 ? (
                <div className="px-4 py-10 text-center text-gray-400 text-sm">
                  <ShoppingCart className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p>No hi ha comandes de compra vinculades a aquest expedient.</p>
                  <p className="mt-1 text-xs">Crea una comanda des del mòdul de Compres i assigna-la a l&apos;expedient <strong>{project.expedientNumber}</strong>.</p>
                </div>
              ) : (
                <table className="w-full text-sm text-muted-foreground">
                  <thead className="bg-gray-50 border-b sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Num. Comanda</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Data</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Proveïdor</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">OT</th>
                      <th className="px-3 py-2 text-center font-medium text-gray-600">Estat</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">Base</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchases.map(po => {
                      const statusColors: Record<string, string> = {
                        DRAFT:             'bg-gray-100 text-gray-600',
                        SENT:              'bg-indigo-100 text-indigo-700',
                        PARTIALLY_RECEIVED:'bg-orange-100 text-orange-700',
                        RECEIVED:          'bg-green-100 text-green-700',
                        CANCELLED:         'bg-red-100 text-red-600',
                      };
                      const statusLabels: Record<string, string> = {
                        DRAFT: 'Esborrany', SENT: 'Enviada',
                        PARTIALLY_RECEIVED: 'Parc. rebuda', RECEIVED: 'Rebuda',
                        CANCELLED: 'Cancel·lada',
                      };
                      const colorClass = statusColors[po.status] || 'bg-gray-100 text-gray-600';
                      return (
                        <tr key={po.id} className="border-b hover:bg-gray-50">
                          <td className="px-3 py-2 font-mono font-medium">
                            {po.orderNumber || po.order_number}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {po.orderDate || po.order_date
                              ? new Date(po.orderDate || po.order_date).toLocaleDateString('ca-ES')
                              : '—'}
                          </td>
                          <td className="px-3 py-2">
                            {po.supplier?.name || po.supplierName || po.supplier_name || '—'}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs text-gray-500">
                            {po.otNumber || po.ot_number || '—'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
                              {statusLabels[po.status] || po.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {Number(po.subtotal || 0).toLocaleString('ca-ES', { minimumFractionDigits: 2 })} €
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            {Number(po.total || 0).toLocaleString('ca-ES', { minimumFractionDigits: 2 })} €
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-100 border-t-2 font-semibold">
                    <tr>
                      <td colSpan={5} className="px-3 py-2 text-right">
                        {purchases.length} comanda{purchases.length !== 1 ? 'es' : ''}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {purchases.reduce((s, po) => s + Number(po.subtotal || 0), 0)
                          .toLocaleString('ca-ES', { minimumFractionDigits: 2 })} €
                      </td>
                      <td className="px-3 py-2 text-right">
                        {purchases.reduce((s, po) => s + Number(po.total || 0), 0)
                          .toLocaleString('ca-ES', { minimumFractionDigits: 2 })} €
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>

            <div className="flex justify-end px-4 py-3 border-t bg-gray-50 flex-shrink-0">
              <Button variant="outline" onClick={() => setShowCompresModal(false)}>
                Tancar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Hores */}
      {showHoresModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Clock className="w-5 h-5" /> Imputació d&apos;hores — Exp. {project.expedientNumber}
              </h3>
              <button onClick={() => setShowHoresModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Formulari d'alta */}
            <div className="px-4 py-3 border-b bg-gray-50 flex-shrink-0">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Nou registre</p>
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-2">
                  <label className="block text-xs text-gray-600 mb-1">Data</label>
                  <Input
                    type="date"
                    value={horesForm.workDate}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setHoresForm(f => ({ ...f, workDate: e.target.value }))}
                    className="text-sm"
                  />
                </div>
                <div className="col-span-3">
                  <label className="block text-xs text-gray-600 mb-1">OT</label>
                  <select
                    value={horesForm.workOrderId}
                    onChange={(e) => setHoresForm(f => ({ ...f, workOrderId: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                  >
                    <option value="">Selecciona...</option>
                    {workOrders.map(ot => (
                      <option key={ot.id} value={ot.id}>{ot.orderNumber} — {ot.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-3">
                  <label className="block text-xs text-gray-600 mb-1">Empleat</label>
                  <select
                    value={horesForm.employeeId}
                    onChange={(e) => setHoresForm(f => ({ ...f, employeeId: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                  >
                    <option value="">Selecciona...</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-gray-600 mb-1">Hores</label>
                  <Input
                    type="number"
                    step="0.25"
                    min="0"
                    value={horesForm.hours || ''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setHoresForm(f => ({ ...f, hours: parseFloat(e.target.value) || 0 }))}
                    placeholder="0,00"
                    className="text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <Button
                    size="sm"
                    onClick={handleAddWorkLog}
                    disabled={savingLog || !horesForm.workOrderId || !horesForm.hours || !horesForm.employeeId}
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Afegir
                  </Button>
                </div>
              </div>
              {horesForm.workOrderId && (
                <div className="mt-2">
                  <Input
                    value={horesForm.description}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setHoresForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Descripció del treball realitzat..."
                    className="text-sm"
                  />
                </div>
              )}
            </div>

            {/* Llista de registres */}
            <div className="flex-1 overflow-y-auto">
              {workLogs.length === 0 ? (
                <div className="px-4 py-8 text-center text-gray-400 text-sm">
                  No hi ha registres d&apos;hores. Afegeix el primer registre amb el formulari superior.
                </div>
              ) : (
                <table className="w-full text-sm text-muted-foreground">
                  <thead className="bg-gray-50 border-b sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Data</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">OT</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Treballador</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">Hores</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Descripció</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {workLogs.map(log => (
                      <tr key={log.id} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 whitespace-nowrap">
                          {new Date(log.workDate).toLocaleDateString('ca-ES')}
                        </td>
                        <td className="px-3 py-2 font-mono whitespace-nowrap">
                          {workOrders.find(o => o.id === log.workOrderId)?.orderNumber || '—'}
                        </td>
                        <td className="px-3 py-2">
                          {log.employee ? `${log.employee.firstName} ${log.employee.lastName}` : '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-medium">
                          {Number(log.hours).toLocaleString('ca-ES', { minimumFractionDigits: 2 })}h
                        </td>
                        <td className="px-3 py-2 text-gray-500 max-w-[180px] truncate">
                          {log.description || '—'}
                        </td>
                        <td className="px-3 py-2">
                          <button onClick={() => handleDeleteWorkLog(log.id)} className="text-red-500 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-100 border-t-2 font-semibold">
                    <tr>
                      <td colSpan={3} className="px-3 py-2 text-right">Total:</td>
                      <td className="px-3 py-2 text-right">
                        {workLogs.reduce((s, l) => s + Number(l.hours), 0)
                          .toLocaleString('ca-ES', { minimumFractionDigits: 2 })}h
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>

            <div className="flex justify-end px-4 py-3 border-t bg-gray-50 flex-shrink-0">
              <Button variant="outline" onClick={() => setShowHoresModal(false)}>
                Tancar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Nova/Editar OT */}
      {showOTModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-sm font-semibold">
                {editingOT ? `Editar OT ${editingOT.orderNumber}` : 'Nova Ordre de Treball'}
              </h3>
              <button onClick={() => setShowOTModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
<label className="block text-sm font-medium text-gray-700 mb-1">Tipus OT *</label>
                  <select
                    value={otFormData.typeId}
                    onChange={(e) => {
                      const selectedType = otTypes.find(t => t.id === Number(e.target.value));
                      setOtFormData({ 
                        ...otFormData, 
                        typeId: Number(e.target.value),
                        name: selectedType?.name || ''
                      });
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value={0}>Selecciona...</option>
                    {otTypes.map((type) => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
              </div>
              {!editingOT && budgetLines.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Línia de pressupost *</label>
                  <select
                    value={otFormData.projectBudgetLineId}
                    onChange={(e) => handleSelectBudgetLine(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="">Selecciona...</option>
                    {budgetLines.map((line) => (
                      <option key={line.id} value={line.id}>
                        {line.concepte}{line.nodeName ? ` — ${line.nodeCode ? line.nodeCode + ' ' : ''}${line.nodeName}` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Determina l'escandall de la OT i pre-omple Prev. Hores/Materials.
                  </p>
                </div>
              )}
              {!editingOT && (() => {
                const selectedLine = budgetLines.find((l) => l.id === otFormData.projectBudgetLineId);
                const sublines = selectedLine?.escandallLines ?? [];
                if (!selectedLine || sublines.length === 0) return null;
                return (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Línia de detall</label>
                    <select
                      value={otFormData.budgetLineIndex ?? ''}
                      onChange={(e) => handleSelectSubline(selectedLine, e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">Tota la línia (sense detallar)</option>
                      {sublines.map((sub, i) => {
                        const badge = badgeFor((sub.childType as any) || 'MATERIAL', sub.nature || 'MATERIAL');
                        return (
                          <option key={i} value={i}>
                            [{badge.text}] {sub.description || `Línia ${i + 1}`}
                          </option>
                        );
                      })}
                    </select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Tria quina línia concreta del desglose (material/mà d&apos;obra/servei) genera aquesta OT — proposa el Tipus i pre-omple Prev. Hores/Materials.
                    </p>
                  </div>
                );
              })()}
              {!editingOT && budgetLines.length === 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Escandall{escandalls.length > 0 ? ' *' : ''}
                  </label>
                  <select
                    value={otFormData.nodeId}
                    onChange={(e) => setOtFormData({ ...otFormData, nodeId: e.target.value })}
                    disabled={escandalls.length === 0}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    <option value="">Selecciona...</option>
                    {escandalls.map((esc) => (
                      <option key={esc.id} value={esc.id}>{esc.code ? `${esc.code} — ` : ''}{esc.name}</option>
                    ))}
                  </select>
                  {escandalls.length === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      El pressupost d'origen no té cap escandall vinculat — es pot crear la OT sense escandall.
                    </p>
                  )}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Operari assignat</label>
                <select
                  value={otFormData.assignedToId}
                  onChange={(e) => setOtFormData({ ...otFormData, assignedToId: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="">Sense assignar</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripcio</label>
                <Input
                  value={otFormData.description}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setOtFormData({ ...otFormData, description: e.target.value })}
                  placeholder="Descripcio de la OT"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unitats</label>
                  <Input
                    type="number"
                    value={otFormData.units}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setOtFormData({ ...otFormData, units: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipus Ut</label>
                  <select
                    value={otFormData.unitType}
                    onChange={(e) => setOtFormData({ ...otFormData, unitType: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="Ut">Ut</option>
                    <option value="m2">m2</option>
                    <option value="ml">ml</option>
                    <option value="Kg">Kg</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prev. Hores</label>
                  <Input
                    type="number"
                    value={otFormData.prevHours}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setOtFormData({ ...otFormData, prevHours: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prev. Import (€)</label>
                <Input
                  type="number"
                  value={otFormData.prevMaterials}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setOtFormData({ ...otFormData, prevMaterials: parseFloat(e.target.value) || 0 })}
                />
              </div>
              {(() => {
                // Material/Serveis/Oficina tècnica no es planifiquen des del Gantt "Per operari"
                // (nomes Fabricació/Muntatge hi son assignables) — son les úniques OT's que
                // necessiten un camp de data manual per apareixer a Planificació > Per
                // projecte/Capacitat.
                const selectedType = otTypes.find((t) => t.id === otFormData.typeId);
                if (!['MAT', 'SRV', 'OFI'].includes(selectedType?.code || '')) return null;
                return (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Data inici prevista</label>
                      <Input
                        type="date"
                        value={otFormData.plannedStartDate}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setOtFormData({ ...otFormData, plannedStartDate: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Data fi prevista</label>
                      <Input
                        type="date"
                        value={otFormData.plannedEndDate}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setOtFormData({ ...otFormData, plannedEndDate: e.target.value })}
                      />
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t bg-gray-50">
              <Button variant="outline" onClick={() => setShowOTModal(false)}>
                Cancel·lar
              </Button>
              <Button onClick={handleSaveOT}>
                <Save className="w-4 h-4 mr-1" /> Desa
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
